import {
  Bell,
  CalendarDays,
  Check,
  ChevronDown,
  Eye,
  FileSpreadsheet,
  FilePlus2,
  GitBranch,
  ListChecks,
  MoreVertical,
  Pencil,
  RefreshCw,
  Search,
  Trash2,
  Upload,
  X,
} from "lucide-react";
import { FormEvent, useEffect, useMemo, useRef, useState } from "react";
import { ExecutionCivilDateInput } from "../features/execution/ExecutionCivilDateInput";
import { DataTable } from "../components/DataTable";
import { ErrorNotice, Loading } from "../components/Feedback";
import { Modal } from "../components/Modal";
import {
  availableFollowUpNodes,
  communicationCatalogs,
  communicationCommitmentAlerts,
  communicationCommitmentCalendar,
  communicationCommitmentDashboard,
  communicationStatistics,
  bulkCreateCommunications,
  createCommunication,
  createCommunicationFollowUp,
  deleteCommunication,
  getCommunicationPdf,
  getCommunication,
  listCommunicationFollowUps,
  listCommunicationDeliverables,
  listCommunicationAreaPersonnel,
  markAllCommunicationCommitmentAlertsRead,
  markCommunicationCommitmentAlertRead,
  relationComEst,
  replaceCommunicationPdf,
  searchCommunications,
  toRows,
  updateCommunication,
} from "../lib/api";
import type { FollowUpNode, PointClassification, BulkCommunicationRecord, CommitmentAlert, CommitmentAlertsResponse, CommitmentDashboard, CommitmentDueStatus, CommunicationAreaPerson, CommunicationCommitment, CommunicationDashboard, CommunicationDashboardStatus, CommunicationDeliverable, CommunicationDetail, CommunicationFollowUp, CommunicationFormValues, CommunicationRecipient, CommunicationPartyPreset, CommunicationPoint, CommunicationPointDraft, CommunicationStatus } from "../lib/api";
import { communicationDisplayTitle, isMinuteDocument, MINUTE_PARTICIPANTS, minuteParticipantSelection, prepareCommunicationValues } from "../lib/communicationForm";
import { downloadXlsx } from "../lib/xlsx";
import { getErrorMessage } from "../lib/supabase";
import { useSession } from "../state/SessionContext";
import type { JsonRecord, WorkContext } from "../types";

type CommunicationFilters = {
  search: string;
  start: string;
  end: string;
  typeIds: string[];
  deliverableIds: string[];
  subjectTypeIds: string[];
  emisor: string;
  frontId: string;
  subfrontIds: string[];
  status: string;
};

const emptyFilters: CommunicationFilters = {
  search: "",
  start: "",
  end: "",
  typeIds: [],
  deliverableIds: [],
  subjectTypeIds: [],
  emisor: "",
  frontId: "",
  subfrontIds: [],
  status: "",
};

const COMMUNICATION_PARTIES: readonly CommunicationPartyPreset[] = ["Contratista", "Residencia", "Supervisión"];
type CommunicationPartySelector = CommunicationPartyPreset | "Otro" | "";
const COMMUNICATION_STATUSES: readonly CommunicationStatus[] = ["Abierto", "En atención", "Cerrado"];
const POINT_STATUSES = ["Abierto", "En atención", "Cerrado"] as const;
const CLASSIFICATIONS: PointClassification[] = ["Apertura de acuerdo", "Informativo", "Seguimiento", "Cierre"];

function recipientLabels(row: JsonRecord) {
  const recipients = row.destinatarios as CommunicationRecipient[] | undefined;
  return recipients?.length ? recipients.map((r) => r.tipo_destinatario === "Otro" ? r.nombre_otro : r.tipo_destinatario).join(", ") : String(row.destinatario || "—");
}

function storedCommunicationStatus(value: unknown): CommunicationStatus {
  const status = String(value ?? "").trim().toLocaleLowerCase("es");
  if (status === "cerrado" || status === "atendido") return "Cerrado";
  if (status === "en atención" || status === "en atencion") return "En atención";
  return "Abierto";
}

function displayStoredCommunicationStatus(value: unknown) {
  return String(value ?? "").trim() ? storedCommunicationStatus(value) : "—";
}

export function formatCivilDate(value: unknown) {
  const match = String(value ?? "").match(/^(\d{4})-(\d{2})-(\d{2})/);
  return match ? `${match[3]}/${match[2]}/${match[1]}` : String(value ?? "—");
}

type BulkParseError = { row: number; message: string };
type BulkParseResult = { records: BulkCommunicationRecord[]; errors: BulkParseError[]; tooMany: boolean };

function normalizeBulkDate(value: string): { value?: string; error?: string } {
  const text = value.trim();
  const iso = text.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  const civil = text.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (!iso && !civil) return { error: "Fecha inválida" };
  const year = Number(iso?.[1] ?? civil?.[3]);
  const month = Number(iso?.[2] ?? civil?.[2]);
  const day = Number(iso?.[3] ?? civil?.[1]);
  const leapYear = year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0);
  const daysInMonth = [31, leapYear ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31][month - 1] || 0;
  if (year < 1 || month < 1 || month > 12 || day < 1 || day > daysInMonth) return { error: "Fecha inválida" };
  const normalized = `${String(year).padStart(4, "0")}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
  if (normalized > localToday()) return { error: "La fecha no puede ser futura" };
  return { value: normalized };
}

export function parseBulkCommunications(text: string): BulkParseResult {
  const sourceRows = text.split(/\r?\n/).map((content, index) => ({ content, row: index + 1 })).filter(({ content }) => content.trim());
  const firstColumns = sourceRows[0]?.content.split("\t").map((value) => value.trim().toLocaleLowerCase("es"));
  const hasHeader = firstColumns?.[0] === "fecha" && firstColumns?.[1] === "folio" && firstColumns?.[2] === "resumen";
  const rows = hasHeader ? sourceRows.slice(1) : sourceRows;
  const records: BulkCommunicationRecord[] = [];
  const errors: BulkParseError[] = [];
  for (const { content, row } of rows) {
    const columns = content.split("\t");
    if (columns.length < 3) { errors.push({ row, message: "Se requieren las columnas FECHA, FOLIO y RESUMEN" }); continue; }
    const date = normalizeBulkDate(columns[0]);
    const folio = columns[1].trim();
    const resumen = columns.slice(2).join("\t").trim();
    if (date.error) errors.push({ row, message: date.error });
    if (!folio) errors.push({ row, message: "Folio vacío" });
    if (!resumen) errors.push({ row, message: "Resumen vacío" });
    if (!date.error && folio && resumen) records.push({ fecha: date.value as string, folio, resumen });
  }
  return { records, errors, tooMany: rows.length > 500 };
}

function localToday() {
  const now = new Date();
  return [now.getFullYear(), String(now.getMonth() + 1).padStart(2, "0"), String(now.getDate()).padStart(2, "0")].join("-");
}

function nextCivilDate(value: unknown) {
  const match = String(value ?? "").match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) return "";
  const date = new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3]) + 1));
  return date.toISOString().slice(0, 10);
}

function arrayFrom(catalog: JsonRecord, keys: string[]) {
  for (const key of keys) if (Array.isArray(catalog[key])) return catalog[key] as JsonRecord[];
  return [];
}

function rowValue(row: JsonRecord) {
  return String(row.id || row.value || row.tipo_doc_id || "");
}

function rowLabel(row: JsonRecord) {
  return String(row.nombre || row.label || row.tipo || row.descripcion || row.folio || "");
}

function stringArray(value: unknown) {
  return Array.isArray(value) ? value.map(String) : [];
}

type MultiSelectOption = { value: string; label: string };

function catalogOptions(rows: JsonRecord[], label = rowLabel): MultiSelectOption[] {
  return rows.map((row) => ({ value: rowValue(row), label: label(row) }));
}

function MultiSelect({
  values,
  options,
  onChange,
  placeholder = "Seleccione una o varias opciones",
  searchPlaceholder = "Filtrar…",
  disabled = false,
  className = "",
}: {
  values: string[];
  options: MultiSelectOption[];
  onChange: (values: string[]) => void;
  placeholder?: string;
  searchPlaceholder?: string;
  disabled?: boolean;
  className?: string;
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [position, setPosition] = useState({ top: 0, left: 0, width: 320 });
  const root = useRef<HTMLDivElement>(null);
  const instanceId = useRef(crypto.randomUUID());
  const selected = options.filter((option) => values.includes(option.value));
  const visible = options.filter((option) => option.label.toLocaleLowerCase("es").includes(search.trim().toLocaleLowerCase("es")));

  useEffect(() => {
    function handlePointerDown(event: PointerEvent) {
      if (open && root.current && !root.current.contains(event.target as Node)) {
        setOpen(false);
        setSearch("");
      }
    }
    function closeOther(event: Event) {
      if ((event as CustomEvent<string>).detail !== instanceId.current) {
        setOpen(false);
        setSearch("");
      }
    }
    document.addEventListener("pointerdown", handlePointerDown, true);
    document.addEventListener("kalite:multiselect-open", closeOther);
    return () => {
      document.removeEventListener("pointerdown", handlePointerDown, true);
      document.removeEventListener("kalite:multiselect-open", closeOther);
    };
  }, [open]);

  function toggle() {
    if (disabled) return;
    if (!open && root.current) {
      document.dispatchEvent(new CustomEvent("kalite:multiselect-open", { detail: instanceId.current }));
      const rect = root.current.getBoundingClientRect();
      const minimumWidth = className.includes("dashboard-front-multiselect") ? 240 : 320;
      const width = Math.min(Math.max(rect.width, minimumWidth), window.innerWidth - 16);
      const left = Math.min(Math.max(8, rect.left), window.innerWidth - width - 8);
      const top = rect.bottom + 352 > window.innerHeight ? Math.max(8, rect.top - 344) : rect.bottom + 5;
      setPosition({ top, left, width });
    }
    setOpen((current) => {
      if (current) setSearch("");
      return !current;
    });
  }

  function toggleValue(value: string) {
    onChange(values.includes(value) ? values.filter((item) => item !== value) : [...values, value]);
  }

  return <div className={`multi-combobox${disabled ? " disabled" : ""}${className ? ` ${className}` : ""}`} ref={root}>
    <button type="button" className="multi-combobox-trigger" aria-expanded={open} aria-haspopup="listbox" disabled={disabled} onClick={toggle}>
      <span className="multi-combobox-value">
        {!selected.length ? <span className="multi-placeholder">{placeholder}</span> : <>
          <span className="multi-chip" title={selected[0].label}>{selected[0].label}<span role="button" aria-label={`Quitar ${selected[0].label}`} tabIndex={0} onClick={(event) => { event.stopPropagation(); toggleValue(selected[0].value); }} onKeyDown={(event) => { if (event.key === "Enter" || event.key === " ") { event.preventDefault(); event.stopPropagation(); toggleValue(selected[0].value); } }}><X size={13} /></span></span>
          {selected.length > 1 && <span className="multi-count">+{selected.length - 1} más</span>}
        </>}
      </span>
      <ChevronDown size={17} />
    </button>
    {open && <div className="multi-combobox-popover" style={position}>
      <div className="multi-search"><Search size={17} /><input autoFocus value={search} onChange={(event) => setSearch(event.target.value)} placeholder={searchPlaceholder} aria-label="Filtrar opciones" /></div>
      <div className="multi-options" role="listbox" aria-multiselectable="true">
        {visible.map((option) => <label className={`multi-option${values.includes(option.value) ? " selected" : ""}`} key={option.value}>
          <input type="checkbox" checked={values.includes(option.value)} onChange={() => toggleValue(option.value)} />
          <span className="multi-check" aria-hidden="true">{values.includes(option.value) && <Check size={14} />}</span>
          <span>{option.label}</span>
        </label>)}
        {!visible.length && <div className="multi-empty">No hay coincidencias.</div>}
      </div>
    </div>}
  </div>;
}

export function CommunicationsPage() {
  const { context } = useSession();
  const [rows, setRows] = useState<JsonRecord[]>([]);
  const [followUps, setFollowUps] = useState<CommunicationFollowUp[]>([]);
  const [catalog, setCatalog] = useState<JsonRecord>({});
  const [filters, setFilters] = useState<CommunicationFilters>(emptyFilters);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<CommunicationDetail | null>(null);
  const [editingLoading, setEditingLoading] = useState(false);
  const editRequest = useRef(0);
  const [viewingPoints, setViewingPoints] = useState<{ parent: JsonRecord; folio: string; detail: CommunicationDetail | null; loading: boolean; error: string; initialPointId?: string; initialRecipientId?: string } | null>(null);
  const pointsRequest = useRef(0);
  const [deleting, setDeleting] = useState<JsonRecord | null>(null);
  const [newFollowUp, setNewFollowUp] = useState<{ parent: JsonRecord; pointId: string | null } | null>(null);
  const [pdf, setPdf] = useState<{ url: string; title: string } | null>(null);
  const [showEstimationRelations, setShowEstimationRelations] = useState(false);
  const [showBulkUpload, setShowBulkUpload] = useState(false);
  const [success, setSuccess] = useState("");
  const [actionBusy, setActionBusy] = useState(false);
  const [tab, setTab] = useState<"list" | "calendar" | "dashboard">("list");
  const [alerts, setAlerts] = useState<CommitmentAlertsResponse>({ noLeidas: 0, items: [] });
  const [alertsOpen, setAlertsOpen] = useState(false);
  const [alertsLoading, setAlertsLoading] = useState(false);
  const [alertsError, setAlertsError] = useState("");

  const types = arrayFrom(catalog, ["tiposDocumento", "tipos_documento", "tipos_doc", "tipos"]);
  const deliverables = ((catalog.entregablesComunicacion as CommunicationDeliverable[] | undefined) || []);
  const fronts = arrayFrom(catalog, ["frentes"]);
  const subfronts = arrayFrom(catalog, ["subfrentes"]);
  const subjects = arrayFrom(catalog, ["tiposAsunto", "tipos_asunto"]);
  const filteredSubfronts = filters.frontId
    ? subfronts.filter((row) => String(row.frenteId || row.frente_id || "") === filters.frontId)
    : [];
  const alertsVisible = ["coordinador", "supervisor"].includes(String(context?.rol || "").trim().toLocaleLowerCase("es"));

  async function loadAlerts() {
    if (!context || !alertsVisible) return;
    setAlertsLoading(true);
    setAlertsError("");
    try {
      setAlerts(await communicationCommitmentAlerts(context));
    } catch (caught) {
      setAlertsError(getErrorMessage(caught));
    } finally {
      setAlertsLoading(false);
    }
  }

  async function load(nextFilters: CommunicationFilters = filters, includeCatalog = false) {
    if (!context) return;
    setLoading(true);
    setError("");
    try {
      const needsCatalog = includeCatalog || !Object.keys(catalog).length;
      const [result, catalogs, fetchedFollowUps, deliverables] = await Promise.all([
        searchCommunications(context, nextFilters),
        needsCatalog ? communicationCatalogs(context) : Promise.resolve(catalog),
        listCommunicationFollowUps(context),
        needsCatalog
          ? listCommunicationDeliverables(context)
          : Promise.resolve((catalog.entregablesComunicacion as CommunicationDeliverable[] | undefined) || []),
      ]);
      const followedCommunicationIds = new Set(
        fetchedFollowUps
          .map((row) => String(row.doc_padre_id || ""))
          .filter(Boolean),
      );
      setFollowUps(fetchedFollowUps);
      setRows(toRows(result).map((row) => ({ ...row, seguimiento: followedCommunicationIds.has(String(row.id)) })));
      setCatalog({ ...(catalogs || {}), entregablesComunicacion: deliverables });
    } catch (caught) {
      setError(getErrorMessage(caught));
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { void load(emptyFilters, true); }, [context]);
  useEffect(() => {
    if (!alertsVisible) {
      setAlerts({ noLeidas: 0, items: [] });
      return;
    }
    void loadAlerts();
    function refreshWhenVisible() {
      if (document.visibilityState === "visible") void loadAlerts();
    }
    document.addEventListener("visibilitychange", refreshWhenVisible);
    return () => document.removeEventListener("visibilitychange", refreshWhenVisible);
  }, [context?.proyecto_id, context?.contrato_id, alertsVisible]);
  const count = useMemo(() => rows.length, [rows]);

  function setFilter<K extends keyof CommunicationFilters>(key: K, value: CommunicationFilters[K]) {
    setFilters((current) => ({
      ...current,
      [key]: value,
      ...(key === "frontId" ? { subfrontIds: [] } : {}),
    }));
  }

  function applyFilters() {
    if (filters.end && filters.start && filters.end < filters.start) {
      setError('La fecha "Hasta" no puede ser anterior a la fecha "Desde".');
      return;
    }
    void load();
  }

  function exportExcel() {
    const text = (value: unknown) => Array.isArray(value) ? value.join(", ") : String(value ?? "");
    downloadXlsx(`comunicaciones_${localToday()}.xlsx`,
      ["Folio", "Fecha", "Área", "Frente", "Subfrente", "Estatus", "Emisor", "Destinatario", "Días transcurridos", "Título", "Resumen", "Entregable", "Asunto"],
      rows.map((row) => [text(row.folio), formatCivilDate(row.fecha), text(row.area), text(row.frente), text(row.subfrente), text(row.estatus).trim() || "Sin estatus", text(row.emisor), recipientLabels(row), text(row.dias_gestion), text(row.titulo), text(row.resumen), text(row.entregable), text(row.tipo_asunto)]),
      [18, 13, 22, 22, 28, 18, 18, 25, 18, 45, 60, 30, 25]);
  }

  async function confirmDelete() {
    if (!deleting) return;
    setActionBusy(true);
    setError("");
    try {
      await deleteCommunication(String(deleting.id));
      setDeleting(null);
      await load();
    } catch (caught) {
      setError(getErrorMessage(caught));
      setDeleting(null);
    } finally {
      setActionBusy(false);
    }
  }

  async function viewPdf(row: JsonRecord) {
    setActionBusy(true);
    setError("");
    try {
      const result = await getCommunicationPdf(String(row.id));
      setPdf({ url: String(result.viewUrl), title: String(result.fileName || row.folio || "Documento PDF") });
    } catch (caught) {
      setError(getErrorMessage(caught));
    } finally {
      setActionBusy(false);
    }
  }

  async function editCommunication(row: JsonRecord) {
    const request = ++editRequest.current;
    setEditingLoading(true);
    setError("");
    try {
      const detail = await getCommunication(String(row.id));
      if (request === editRequest.current) setEditing(detail);
    } catch (caught) {
      if (request === editRequest.current) setError(getErrorMessage(caught));
    } finally {
      if (request === editRequest.current) setEditingLoading(false);
    }
  }

  function cancelEditLoading() {
    editRequest.current += 1;
    setEditingLoading(false);
  }

  async function viewCommunicationPoints(row: JsonRecord, initialPointId?: string, initialRecipientId?: string) {
    const request = ++pointsRequest.current;
    setViewingPoints({ parent: row, folio: String(row.folio || ""), detail: null, loading: true, error: "", initialPointId, initialRecipientId });
    try {
      const detail = await getCommunication(String(row.id));
      if (request === pointsRequest.current) {
        setViewingPoints({
          parent: { ...row, ...(detail as unknown as JsonRecord) },
          folio: String(row.folio || detail.folio || ""),
          detail,
          loading: false,
          error: "",
          initialPointId, initialRecipientId,
        });
      }
    } catch (caught) {
      if (request === pointsRequest.current) setViewingPoints({ parent: row, folio: String(row.folio || ""), detail: null, loading: false, error: getErrorMessage(caught), initialPointId, initialRecipientId });
    }
  }

  function closeCommunicationPoints() {
    pointsRequest.current += 1;
    setViewingPoints(null);
  }

  async function openCommitment(item: CommunicationCommitment) {
    await viewCommunicationPoints({ id: item.comunicacionId, folio: item.folio || "" }, item.puntoId || undefined, item.destinatarioId || undefined);
  }

  async function openAlert(alert: CommitmentAlert) {
    if (!context) return;
    try {
      const wasUnread = !alert.leidaAt;
      if (wasUnread) await markCommunicationCommitmentAlertRead(alert.id);
      if (wasUnread) {
        setAlerts((current) => ({
          noLeidas: Math.max(0, current.noLeidas - 1),
          items: current.items.map((item) => item.id === alert.id ? { ...item, leidaAt: new Date().toISOString() } : item),
        }));
      }
      setAlertsOpen(false);
      await viewCommunicationPoints({ id: alert.comunicacionId, folio: alert.folio || "" }, alert.puntoId || undefined, alert.destinatarioId || undefined);
    } catch (caught) {
      setAlertsError(getErrorMessage(caught));
    }
  }

  async function markAllAlertsRead() {
    if (!context) return;
    setAlertsLoading(true);
    setAlertsError("");
    try {
      await markAllCommunicationCommitmentAlertsRead(context);
      await loadAlerts();
    } catch (caught) {
      setAlertsError(getErrorMessage(caught));
      setAlertsLoading(false);
    }
  }

  return <>
    <div className="page-heading">
      <div><span className="eyebrow">CONTROL DOCUMENTAL</span><h1>Comunicaciones</h1><p>Consulta y registra la correspondencia oficial del contrato.</p></div>
      <div className="heading-actions">
        {alertsVisible && <button className="button secondary commitment-alert-button" aria-label="Alertas de compromisos" onClick={() => setAlertsOpen(true)}><Bell size={17} /> Alertas{alerts.noLeidas > 0 && <span>{alerts.noLeidas}</span>}</button>}
        <button className="button secondary" onClick={() => setShowEstimationRelations(true)}><FileSpreadsheet size={17} /> Relación con estimaciones</button>
        <button className="button secondary" onClick={() => setShowBulkUpload(true)}><Upload size={17} /> Carga masiva</button>
        <button className="button primary" onClick={() => setCreating(true)}><FilePlus2 size={17} /> Nueva comunicación</button>
      </div>
    </div>
    <div className="tabs communications-tabs"><button className={tab === "list" ? "active" : ""} onClick={() => setTab("list")}>Listado</button><button className={tab === "calendar" ? "active" : ""} onClick={() => setTab("calendar")}>Calendario</button><button className={tab === "dashboard" ? "active" : ""} onClick={() => setTab("dashboard")}>Dashboard</button></div>
    {success && <div className="notice success communications-success">{success}</div>}
    {tab === "list" ? <section className="panel communications-list">
      <form className="filter-grid communications-filter-grid" onSubmit={(event) => { event.preventDefault(); applyFilters(); }}>
        <div className="search-box filter-search communications-filter-search"><Search size={17} /><input aria-label="Búsqueda" placeholder="Resumen, folio o destinatario…" value={filters.search} onChange={(event) => setFilter("search", event.target.value)} /></div>
        <div className="filter-field communications-filter-type"><span className="field-label">Tipo de comunicación</span><MultiSelect values={filters.typeIds} options={catalogOptions(types)} onChange={(value) => setFilter("typeIds", value)} /></div>
        <div className="filter-field communications-filter-deliverable"><span className="field-label">Entregable</span><MultiSelect values={filters.deliverableIds} options={deliverables.map((deliverable) => ({ value: deliverable.id, label: deliverable.nombre }))} onChange={(value) => setFilter("deliverableIds", value)} /></div>
        <label className="filter-field communications-filter-emisor"><span className="field-label">Emisor</span><select value={filters.emisor} onChange={(event) => setFilter("emisor", event.target.value)}><option value="">Todos</option><option value="Supervisión">Supervisión</option><option value="Residencia">Residencia</option><option value="Contratista">Contratista</option><option value="Otro">Otro</option></select></label>
        <label className="filter-field communications-filter-front"><span className="field-label">Frente</span><select value={filters.frontId} onChange={(event) => setFilter("frontId", event.target.value)}><option value="">Todos</option>{fronts.map((row) => <option key={rowValue(row)} value={rowValue(row)}>{rowLabel(row)}</option>)}</select></label>
        <div className="filter-field communications-filter-subfront"><span className="field-label">Subfrente</span><MultiSelect disabled={!filters.frontId} values={filters.subfrontIds} options={catalogOptions(filteredSubfronts)} onChange={(value) => setFilter("subfrontIds", value)} placeholder={filters.frontId ? "Seleccione uno o varios" : "Seleccione primero un frente"} /></div>
        <div className="filter-field communications-filter-subject"><span className="field-label">Tipo de asunto</span><MultiSelect values={filters.subjectTypeIds} options={catalogOptions(subjects)} onChange={(value) => setFilter("subjectTypeIds", value)} /></div>
        <label className="filter-field status-filter communications-filter-status"><span className="field-label">Estatus</span><select value={filters.status} onChange={(event) => setFilter("status", event.target.value)}><option value="">Todos los estatus</option>{COMMUNICATION_STATUSES.map((status) => <option key={status} value={status}>{status}</option>)}</select></label>
        <label className="filter-date communications-filter-from"><span className="field-label">Desde</span><ExecutionCivilDateInput max={filters.end || localToday()} value={filters.start} onChange={(value) => setFilter("start", value)} /></label>
        <label className="filter-date communications-filter-to"><span className="field-label">Hasta</span><ExecutionCivilDateInput min={filters.start || undefined} max={localToday()} value={filters.end} onChange={(value) => setFilter("end", value)} /></label>
        <div className="filter-actions"><button className="button secondary">Aplicar filtros</button><button type="button" className="button ghost" onClick={() => { setFilters(emptyFilters); void load(emptyFilters); }}>Limpiar</button><button type="button" className="icon-button" title="Actualizar" onClick={() => void load(filters, true)}><RefreshCw size={18} /></button></div>
      </form>
      <div className="section-meta"><strong>Listado de comunicaciones</strong><span>{count} registros</span><button type="button" className="button secondary" disabled={loading || !rows.length} onClick={exportExcel}><FileSpreadsheet size={17}/> Descargar Excel</button></div>
      {error && <ErrorNotice message={error} />}
      {loading ? <Loading /> : <DataTable
        rows={rows}
        columns={["folio", "fecha", "titulo", "estatus", "emisor", "destinatario", "dias_gestion"]}
        columnLabels={{ titulo: "Título", dias_gestion: "Días transcurridos" }}
        tableClassName="communications-table"
        actionsColumnClassName="communications-actions"
        renderCell={(row, key) => key === "destinatario" ? recipientLabels(row) : key === "dias_gestion" ? <span title={String(row.estatus) === "Cerrado" ? "Días desde la emisión hasta el cierre" : "Días transcurridos desde la emisión"}>{row.dias_gestion == null ? "—" : `${String(row.dias_gestion)} días`}</span> : key === "fecha" ? formatCivilDate(row.fecha) : key === "folio" ? <span className="communication-folio">{String(row.folio ?? "—")}</span> : key === "titulo" ? <span className="communication-summary">{communicationDisplayTitle(row)}</span> : key === "estatus" ? displayStoredCommunicationStatus(row.estatus) : undefined}
        actions={(row) => <RowActions
          row={row}
          busy={actionBusy || editingLoading || viewingPoints?.loading === true}
          onEdit={() => void editCommunication(row)}
          onPoints={() => void viewCommunicationPoints(row)}
          onDelete={() => setDeleting(row)}
          onPdf={() => void viewPdf(row)}
        />}
      />}
    </section> : tab === "calendar" && context ? <CommitmentCalendar context={context} catalog={catalog} onOpen={(item) => void openCommitment(item)} /> : context && <CommunicationsDashboardArea context={context} catalog={catalog} types={types} fronts={fronts} />}
    {creating && context && <CommunicationModal catalog={catalog} onClose={() => setCreating(false)} onSaved={async () => { setCreating(false); await load(); }} />}
    {editingLoading && <Modal title="Cargando comunicación" onClose={cancelEditLoading}><Loading /></Modal>}
    {editing && <CommunicationModal catalog={catalog} communication={editing} onClose={() => setEditing(null)} onSaved={async () => { setEditing(null); await load(); }} />}
    {viewingPoints && <CommunicationPointsModal
      onNavigate={(row, pointId) => void viewCommunicationPoints(row, pointId)}
      state={viewingPoints}
      catalog={catalog}
      followUps={followUps}
      onClose={closeCommunicationPoints}
      onCreateNew={(pointId) => {
        setNewFollowUp({ parent: viewingPoints.parent, pointId });
        closeCommunicationPoints();
      }}
      onPdf={(row) => void viewPdf(row)}
      onSaved={async () => { await load(filters, true); await loadAlerts(); }}
    />}
    {newFollowUp && <CommunicationModal catalog={catalog} followUpParent={newFollowUp.parent} followUpPointId={newFollowUp.pointId} onClose={() => setNewFollowUp(null)} onSaved={async () => { setNewFollowUp(null); await load(filters, true); }} />}
    {showEstimationRelations && context && <CommunicationsEstimationsModal catalog={catalog} onClose={() => setShowEstimationRelations(false)} />}
    {showBulkUpload && context && <BulkCommunicationsModal catalog={catalog} onClose={() => setShowBulkUpload(false)} onImported={async (count) => { setShowBulkUpload(false); setSuccess(`${count} comunicaciones importadas correctamente.`); setTab("list"); await load(filters); }} />}
    {deleting && <Modal title="Eliminar comunicación" onClose={() => setDeleting(null)}><div className="confirm-body"><p>Se eliminará la comunicación <strong>{String(deleting.folio || "")}</strong> y, si existe, su PDF asociado. Esta operación respetará las autorizaciones configuradas en el backend.</p><div className="modal-actions"><button className="button ghost" onClick={() => setDeleting(null)}>Cancelar</button><button className="button primary" disabled={actionBusy} onClick={() => void confirmDelete()}>{actionBusy ? "Eliminando…" : "Eliminar"}</button></div></div></Modal>}
    {alertsOpen && <CommitmentAlertsPanel data={alerts} loading={alertsLoading} error={alertsError} onClose={() => setAlertsOpen(false)} onOpen={(alert) => void openAlert(alert)} onMarkAll={() => void markAllAlertsRead()} />}
    {pdf && <Modal title={pdf.title} onClose={() => setPdf(null)}><iframe className="pdf-frame" src={pdf.url} title={pdf.title} /></Modal>}
  </>;
}

function BulkCommunicationsModal({ catalog, onClose, onImported }: { catalog: JsonRecord; onClose: () => void; onImported: (count: number) => Promise<void> }) {
  const { context, permissions } = useSession();
  const isAdmin = String(context?.rol || "").trim().toLocaleLowerCase("es") === "admin";
  const assignedPermission = permissions.find((permission) => permission.proyecto_id === context?.proyecto_id && permission.contrato_id === context?.contrato_id && permission.area_id);
  const assignedAreaId = context?.area_id || assignedPermission?.area_id || "";
  const assignedAreaName = context?.area_nombre || assignedPermission?.area_nombre || "Área no asignada";
  const areas = arrayFrom(catalog, ["areas"]);
  const [areaId, setAreaId] = useState("");
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const parsed = useMemo(() => parseBulkCommunications(text), [text]);
  const resolvedAreaId = isAdmin ? areaId : assignedAreaId;
  const resolvedAreaName = isAdmin ? rowLabel(areas.find((area) => rowValue(area) === areaId) || {}) : assignedAreaName;
  const canImport = Boolean(resolvedAreaId && parsed.records.length && !parsed.errors.length && !parsed.tooMany && !busy);

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (!context || !canImport) return;
    setBusy(true); setError("");
    try {
      const result = await bulkCreateCommunications(context, resolvedAreaId, parsed.records);
      await onImported(Number(result.insertados ?? parsed.records.length));
    } catch (caught) {
      setError(getErrorMessage(caught));
    } finally {
      setBusy(false);
    }
  }

  return <Modal title="Carga masiva de comunicaciones" className="modal-wide bulk-communications-modal" onClose={busy ? () => undefined : onClose}>
    <form className="bulk-communications-form" onSubmit={submit}>
      {error && <div className="notice error">{error}</div>}
      <div className="bulk-intro"><p>Copie desde Excel o Google Sheets las columnas <strong>FECHA, FOLIO y RESUMEN</strong>, separadas por tabuladores. Puede incluir la fila de encabezados.</p><code>15/08/2026&nbsp;&nbsp; SUP-001&nbsp;&nbsp; Resumen de la comunicación</code></div>
      {isAdmin ? <label>Área<select value={areaId} onChange={(event) => setAreaId(event.target.value)} required><option value="">Selecciona…</option>{areas.map((area) => <option key={rowValue(area)} value={rowValue(area)}>{rowLabel(area)}</option>)}</select></label> : <div className="bulk-readonly-area"><span className="field-label">Área</span><strong>{assignedAreaName}</strong></div>}
      <label>Datos para importar<textarea rows={9} value={text} onChange={(event) => { setText(event.target.value); setError(""); }} placeholder={"FECHA\tFOLIO\tRESUMEN\n15/08/2026\tSUP-001\tSe solicitó la entrega del programa actualizado."} /></label>
      <div className="bulk-validation-summary"><strong>{parsed.records.length} registros válidos</strong><span className={parsed.errors.length || parsed.tooMany ? "has-errors" : ""}>{parsed.errors.length + (parsed.tooMany ? 1 : 0)} registros con errores</span></div>
      {(parsed.errors.length > 0 || parsed.tooMany) && <div className="bulk-errors" role="alert">{parsed.tooMany && <div>El lote supera el máximo de 500 registros.</div>}{parsed.errors.map((item, index) => <div key={`${item.row}-${index}`}>Fila {item.row} — {item.message}</div>)}</div>}
      {parsed.records.length > 0 && <div className="bulk-preview"><table><thead><tr><th>Fecha</th><th>Folio</th><th>Resumen</th></tr></thead><tbody>{parsed.records.map((record, index) => <tr key={`${record.folio}-${index}`}><td>{formatCivilDate(record.fecha)}</td><td><span className="communication-folio">{record.folio}</span></td><td><span className="wrap-cell">{record.resumen}</span></td></tr>)}</tbody></table></div>}
      <div className="bulk-confirmation">{parsed.records.length > 0 && resolvedAreaId ? `Se crearán ${parsed.records.length} comunicaciones en el área ${resolvedAreaName || "seleccionada"}.` : "Pegue registros válidos y seleccione el área para continuar."}</div>
      <div className="modal-actions"><button type="button" className="button ghost" disabled={busy} onClick={onClose}>Cancelar</button><button className="button primary" disabled={!canImport}>{busy ? "Importando…" : `Importar ${parsed.records.length} comunicaciones`}</button></div>
    </form>
  </Modal>;
}

const COMMITMENT_DUE_STATUSES: readonly CommitmentDueStatus[] = ["Vencido", "Vence hoy", "Próximo a vencer", "En plazo", "Sin fecha", "Cerrado"];
const dueStatusClass: Record<CommitmentDueStatus, string> = {
  "Vencido": "critical", "Vence hoy": "warning", "Próximo a vencer": "preventive", "En plazo": "positive", "Sin fecha": "neutral", "Cerrado": "closed",
};

function civilParts(value: string) {
  const match = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  return match ? { year: Number(match[1]), month: Number(match[2]), day: Number(match[3]) } : null;
}
function localDateFromCivil(value: string) { const part = civilParts(value); return part ? new Date(part.year, part.month - 1, part.day, 12) : new Date(); }
function civilFromDate(value: Date) { return `${value.getFullYear()}-${String(value.getMonth() + 1).padStart(2, "0")}-${String(value.getDate()).padStart(2, "0")}`; }
function shiftCivil(value: string, days: number) { const date = localDateFromCivil(value); date.setDate(date.getDate() + days); return civilFromDate(date); }
function mondayOf(value: string) { const date = localDateFromCivil(value); const day = date.getDay() || 7; date.setDate(date.getDate() - day + 1); return civilFromDate(date); }
function civilDayDifference(left: string, right: string) { const a = civilParts(left); const b = civilParts(right); return a && b ? Math.round((Date.UTC(a.year, a.month - 1, a.day) - Date.UTC(b.year, b.month - 1, b.day)) / 86400000) : 0; }
function monthRange(anchor: string) { const part = civilParts(anchor) || civilParts(localToday())!; const start = `${part.year}-${String(part.month).padStart(2, "0")}-01`; const endDate = new Date(part.year, part.month, 0, 12); return { start, end: civilFromDate(endDate) }; }
function permittedAreas(catalog: JsonRecord, context: WorkContext) {
  const areas = arrayFrom(catalog, ["areas"]);
  if (String(context.rol || "").toLocaleLowerCase("es") === "admin") return areas;
  const hasRoleMetadata = areas.some((area) => area.rol || area.role || area.rol_asignado || area.roles || area.puede_ver);
  return hasRoleMetadata
    ? areas.filter((area) => Boolean(area.rol || area.role || area.rol_asignado || (Array.isArray(area.roles) && area.roles.length) || area.puede_ver))
    : areas.filter((area) => rowValue(area) === String(context.area_id || ""));
}

type CalendarMode = "month" | "week" | "agenda";
type CommitmentFilters = { areaId: string; frontId: string; subfrontId: string; dueStatus: CommitmentDueStatus | "" };
const emptyCommitmentFilters: CommitmentFilters = { areaId: "", frontId: "", subfrontId: "", dueStatus: "" };

function CommitmentCalendar({ context, catalog, onOpen }: { context: WorkContext; catalog: JsonRecord; onOpen: (item: CommunicationCommitment) => void }) {
  const [mode, setMode] = useState<CalendarMode>(() => window.matchMedia("(max-width: 620px)").matches ? "agenda" : "month");
  const [anchor, setAnchor] = useState(localToday());
  const [filters, setFilters] = useState(emptyCommitmentFilters);
  const [items, setItems] = useState<CommunicationCommitment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const areas = permittedAreas(catalog, context);
  const fronts = arrayFrom(catalog, ["frentes"]);
  const subfronts = arrayFrom(catalog, ["subfrentes"]).filter((row) => !filters.frontId || String(row.frenteId || row.frente_id || "") === filters.frontId);
  const range = mode === "month" ? monthRange(anchor) : mode === "week" ? { start: mondayOf(anchor), end: shiftCivil(mondayOf(anchor), 6) } : { start: shiftCivil(anchor, -30), end: shiftCivil(anchor, 90) };

  useEffect(() => {
    let current = true; setLoading(true); setError("");
    const dated = communicationCommitmentCalendar(context, { start: range.start, end: range.end, ...filters });
    const request = mode === "agenda" && !filters.dueStatus
      ? Promise.all([
          dated,
          communicationCommitmentCalendar(context, { start: range.start, end: range.end, ...filters, dueStatus: "Sin fecha" }),
        ]).then(([datedRows, undatedRows]) => {
          const unique = new Map<string, CommunicationCommitment>();
          [...datedRows, ...undatedRows].forEach((item) => unique.set(`${item.comunicacionId}:${item.puntoId || item.destinatarioId}`, item));
          return [...unique.values()];
        })
      : dated;
    request
      .then((result) => { if (current) setItems(result || []); })
      .catch((caught) => { if (current) setError(getErrorMessage(caught)); })
      .finally(() => { if (current) setLoading(false); });
    return () => { current = false; };
  }, [context, mode, range.start, range.end, filters.areaId, filters.frontId, filters.subfrontId, filters.dueStatus]);

  function setFilter<K extends keyof CommitmentFilters>(key: K, value: CommitmentFilters[K]) {
    setFilters((current) => ({ ...current, [key]: value, ...(key === "frontId" ? { subfrontId: "" } : {}) }));
  }
  function navigate(direction: number) { setAnchor((current) => { const date = localDateFromCivil(current); if (mode === "week") date.setDate(date.getDate() + direction * 7); else date.setMonth(date.getMonth() + direction, 1); return civilFromDate(date); }); }
  const anchorDate = localDateFromCivil(anchor);
  const title = mode === "month" ? anchorDate.toLocaleDateString("es-MX", { month: "long", year: "numeric" }) : `${formatCivilDate(range.start)} — ${formatCivilDate(range.end)}`;
  return <section className="commitment-calendar-page">
    <section className="panel commitment-toolbar">
      <div className="commitment-calendar-heading"><div><CalendarDays size={20}/><strong>Calendario de compromisos</strong></div><div className="calendar-navigation"><button className="icon-button" aria-label="Periodo anterior" onClick={() => navigate(-1)}>‹</button><strong>{title}</strong><button className="icon-button" aria-label="Periodo siguiente" onClick={() => navigate(1)}>›</button><button className="button ghost small" onClick={() => setAnchor(localToday())}>Hoy</button></div></div>
      <div className="commitment-view-tabs" role="tablist" aria-label="Vista del calendario">{(["month", "week", "agenda"] as CalendarMode[]).map((value) => <button key={value} className={mode === value ? "active" : ""} onClick={() => setMode(value)}>{value === "month" ? "Mes" : value === "week" ? "Semana" : "Agenda"}</button>)}</div>
      <div className="commitment-filter-grid">
        <label>Área<select value={filters.areaId} onChange={(event) => setFilter("areaId", event.target.value)}><option value="">Todas las áreas</option>{areas.map((row) => <option key={rowValue(row)} value={rowValue(row)}>{rowLabel(row)}</option>)}</select></label>
        <label>Frente<select value={filters.frontId} onChange={(event) => setFilter("frontId", event.target.value)}><option value="">Todos los frentes</option>{fronts.map((row) => <option key={rowValue(row)} value={rowValue(row)}>{rowLabel(row)}</option>)}</select></label>
        <label>Subfrente<select disabled={!filters.frontId} value={filters.subfrontId} onChange={(event) => setFilter("subfrontId", event.target.value)}><option value="">Todos los subfrentes</option>{subfronts.map((row) => <option key={rowValue(row)} value={rowValue(row)}>{rowLabel(row)}</option>)}</select></label>
        <label>Estado<select value={filters.dueStatus} onChange={(event) => setFilter("dueStatus", event.target.value as CommitmentDueStatus | "")}><option value="">Todos los estados</option>{COMMITMENT_DUE_STATUSES.map((status) => <option key={status}>{status}</option>)}</select></label>
      </div>
      <div className="commitment-quick-filters"><span>Filtros rápidos</span>{(["Vencido", "Vence hoy", "Próximo a vencer", "Sin fecha"] as CommitmentDueStatus[]).map((status) => <button key={status} className={filters.dueStatus === status ? "active" : ""} onClick={() => setFilter("dueStatus", filters.dueStatus === status ? "" : status)}>{status === "Próximo a vencer" ? "Próximos 3 días" : status}</button>)}<button onClick={() => setFilters(emptyCommitmentFilters)}>Limpiar</button></div>
    </section>
    {error && <ErrorNotice message={error}/>} {loading ? <section className="panel"><Loading/></section> : !items.length ? <section className="panel empty-dashboard">No hay compromisos para los filtros seleccionados.</section> : mode === "month" ? <CommitmentMonth items={items} anchor={anchor} onOpen={onOpen}/> : mode === "week" ? <CommitmentWeek items={items} anchor={anchor} onOpen={onOpen}/> : <CommitmentAgenda items={items} onOpen={onOpen}/>}
  </section>;
}

function DueBadge({ status }: { status: CommitmentDueStatus }) { return <span className={`commitment-due due-${dueStatusClass[status]}`}>{status}</span>; }
function CommitmentMonth({ items, anchor, onOpen }: { items: CommunicationCommitment[]; anchor: string; onOpen: (item: CommunicationCommitment) => void }) {
  const range = monthRange(anchor); const gridStart = mondayOf(range.start); const dates = Array.from({ length: 42 }, (_, index) => shiftCivil(gridStart, index));
  return <section className="panel commitment-month"><div className="calendar-weekdays">{["Lun", "Mar", "Mié", "Jue", "Vie", "Sáb", "Dom"].map((day) => <strong key={day}>{day}</strong>)}</div><div className="calendar-month-grid">{dates.map((date) => { const dayItems = items.filter((item) => item.fechaCompromiso === date); return <div className={`calendar-day${date.slice(0, 7) !== range.start.slice(0, 7) ? " outside" : ""}${date === localToday() ? " today" : ""}`} key={date}><header><span>{Number(date.slice(8))}</span>{dayItems.length > 0 && <small>{dayItems.length}</small>}</header>{dayItems.slice(0, 3).map((item) => <button key={`${item.comunicacionId}:${item.puntoId || item.destinatarioId}`} className={`calendar-event due-${dueStatusClass[item.estadoVencimiento]}`} onClick={() => onOpen(item)}><strong>{item.folio}</strong><span>{item.tipoCompromiso === "respuesta_comunicacion" ? `Respuesta de ${item.destinatario || "destinatario"}` : item.punto}</span></button>)}{dayItems.length > 3 && <span className="calendar-more">+ {dayItems.length - 3} más</span>}</div>; })}</div></section>;
}
function CommitmentWeek({ items, anchor, onOpen }: { items: CommunicationCommitment[]; anchor: string; onOpen: (item: CommunicationCommitment) => void }) {
  const start = mondayOf(anchor); const dates = Array.from({ length: 7 }, (_, index) => shiftCivil(start, index));
  return <section className="panel commitment-week">{dates.map((date) => <div className={date === localToday() ? "today" : ""} key={date}><header><strong>{localDateFromCivil(date).toLocaleDateString("es-MX", { weekday: "short" })}</strong><span>{formatCivilDate(date)}</span></header>{items.filter((item) => item.fechaCompromiso === date).map((item) => <button className="week-commitment" key={`${item.comunicacionId}:${item.puntoId || item.destinatarioId}`} onClick={() => onOpen(item)}><strong>{item.folio}</strong><span>{item.tipoCompromiso === "respuesta_comunicacion" ? `Respuesta de ${item.destinatario || "destinatario"}` : item.punto}</span><small>{item.responsables.join(", ") || "Sin responsable"}</small><DueBadge status={item.estadoVencimiento}/></button>)}</div>)}</section>;
}
function CommitmentAgenda({ items, onOpen }: { items: CommunicationCommitment[]; onOpen: (item: CommunicationCommitment) => void }) {
  const today = localToday(); const groups: Array<[string, (item: CommunicationCommitment) => boolean]> = [["Vencidos", (item) => item.estadoVencimiento === "Vencido"], ["Hoy", (item) => item.estadoVencimiento !== "Cerrado" && item.fechaCompromiso === today], ["Mañana", (item) => item.estadoVencimiento !== "Cerrado" && item.fechaCompromiso === shiftCivil(today, 1)], ["Próximos 3 días", (item) => Boolean(item.estadoVencimiento !== "Cerrado" && item.fechaCompromiso && civilDayDifference(item.fechaCompromiso, today) >= 2 && civilDayDifference(item.fechaCompromiso, today) <= 3)], ["Posteriores", (item) => Boolean(item.estadoVencimiento !== "Cerrado" && item.fechaCompromiso && civilDayDifference(item.fechaCompromiso, today) > 3)], ["Compromisos sin fecha", (item) => !item.fechaCompromiso], ["Cerrados", (item) => item.estadoVencimiento === "Cerrado" && Boolean(item.fechaCompromiso)]];
  return <section className="commitment-agenda">{groups.map(([label, predicate]) => { const rows = items.filter(predicate).sort((a, b) => String(a.fechaCompromiso || "9999").localeCompare(String(b.fechaCompromiso || "9999"))); return rows.length ? <section className="panel agenda-group" key={label}><h3>{label}<span>{rows.length}</span></h3><div>{rows.map((item) => <button className="agenda-commitment" key={`${item.comunicacionId}:${item.puntoId || item.destinatarioId}`} onClick={() => onOpen(item)}><div><span>{item.fechaCompromiso ? formatCivilDate(item.fechaCompromiso) : "Sin fecha"}</span><strong>{item.folio}</strong><small>{item.titulo || "Sin título"}</small></div><p>{item.tipoCompromiso === "respuesta_comunicacion" ? `Respuesta de ${item.destinatario || "destinatario"}` : item.punto}</p><div><span>{item.area || "—"}</span><span>{item.frente || "Todos los frentes"}</span><span>{item.responsables.join(", ") || "Sin responsable"}</span><span>{item.totalSeguimientos} seguimientos</span></div><DueBadge status={item.estadoVencimiento}/></button>)}</div></section> : null; })}</section>;
}

function CommunicationsDashboardArea({ context, catalog, types, fronts }: { context: WorkContext; catalog: JsonRecord; types: JsonRecord[]; fronts: JsonRecord[] }) {
  const [view, setView] = useState<"communications" | "commitments">("communications");
  return <><div className="dashboard-subtabs"><button className={view === "communications" ? "active" : ""} onClick={() => setView("communications")}>Comunicaciones</button><button className={view === "commitments" ? "active" : ""} onClick={() => setView("commitments")}>Cumplimiento de compromisos</button></div>{view === "communications" ? <CommunicationsDashboard context={context} types={types} fronts={fronts}/> : <CommitmentDashboardView context={context} catalog={catalog}/>}</>;
}

function CommitmentDashboardView({ context, catalog }: { context: WorkContext; catalog: JsonRecord }) {
  const [areaId, setAreaId] = useState("");
  const [data, setData] = useState<CommitmentDashboard | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const areas = permittedAreas(catalog, context);

  useEffect(() => {
    let current = true;
    setLoading(true); setError("");
    communicationCommitmentDashboard(context, areaId)
      .then((result) => { if (current) setData(result); })
      .catch((caught) => { if (current) setError(getErrorMessage(caught)); })
      .finally(() => { if (current) setLoading(false); });
    return () => { current = false; };
  }, [context, areaId]);

  const safe: CommitmentDashboard = data || {
    fechaCorte: localToday(),
    kpis: { totalCompromisos: 0, abiertos: 0, cerrados: 0, vencidos: 0, venceHoy: 0, proximos3Dias: 0, sinFecha: 0, porcentajeVencidos: 0, promedioDiasAtraso: 0 },
    porEstado: [], porArea: [], porFrente: [], porSemana: [], focoAtencion: null,
  };
  const totalStates = safe.porEstado.reduce((sum, row) => sum + Number(row.total || 0), 0);
  const stateRows = safe.porEstado.map((row) => ({
    etiqueta: row.estado,
    total: Number(row.total || 0),
    porcentaje: totalStates ? Math.round((Number(row.total || 0) / totalStates) * 10000) / 100 : 0,
  }));

  return <section className="dashboard-stack commitment-dashboard">
    <section className="panel commitment-dashboard-filter"><label>Área<select value={areaId} onChange={(event) => setAreaId(event.target.value)}><option value="">Todas las áreas</option>{areas.map((area) => <option key={rowValue(area)} value={rowValue(area)}>{rowLabel(area)}</option>)}</select></label></section>
    {error && <ErrorNotice message={error}/>}
    {loading ? <section className="panel"><Loading/></section> : <>
      <div className="commitment-kpis">
        <Kpi title="Total de compromisos" value={safe.kpis.totalCompromisos}/>
        <Kpi title="Abiertos" value={safe.kpis.abiertos}/>
        <Kpi title="Cerrados" value={safe.kpis.cerrados}/>
        <Kpi title="Vencidos" value={safe.kpis.vencidos} detail={`${safe.kpis.porcentajeVencidos} % de abiertos con fecha`}/>
        <Kpi title="Vence hoy" value={safe.kpis.venceHoy}/>
        <Kpi title="Próximos 3 días" value={safe.kpis.proximos3Dias}/>
        <Kpi title="Sin fecha" value={safe.kpis.sinFecha}/>
        <Kpi title="Promedio de días de atraso" value={safe.kpis.promedioDiasAtraso}/>
      </div>
      <article className="kpi-card commitment-focus"><span>Foco de atención</span><strong>{safe.focoAtencion?.frente || "Sin compromisos vencidos"}</strong>{safe.focoAtencion && <small>{safe.focoAtencion.vencidos} compromisos vencidos</small>}</article>
      <div className="commitment-chart-grid">
        <DonutCard title="Estado de compromisos" rows={stateRows} colorFor={(status) => commitmentStatusColor(status)}/>
        <SimpleBarCard title="Compromisos por Área" rows={safe.porArea.map((row) => ({ label: row.area, value: row.total, detail: `${row.abiertos} abiertos · ${row.vencidos} vencidos` }))}/>
        <SimpleBarCard title="Vencidos por Frente" rows={safe.porFrente.map((row) => ({ label: row.frente, value: row.vencidos }))}/>
        <SimpleBarCard title="Vencimientos por semana" rows={safe.porSemana.slice(0, 8).map((row) => ({ label: formatCivilDate(row.semanaInicio), value: row.total }))}/>
      </div>
    </>}
  </section>;
}
function commitmentStatusColor(status: string) { const key = status.trim().toLocaleLowerCase("es"); if (key === "abierto") return "#4b91c7"; if (key === "cerrado") return "#24745f"; const normalized = COMMITMENT_DUE_STATUSES.find((item) => item.toLocaleLowerCase("es") === key); return normalized ? ({ critical: "#b64137", warning: "#c27a18", preventive: "#b18a22", positive: "#24745f", neutral: "#64748b", closed: "#4c645b" } as Record<string, string>)[dueStatusClass[normalized]] || "#64748b" : "#64748b"; }
function SimpleBarCard({ title, rows }: { title: string; rows: Array<{ label: string; value: number; detail?: string }> }) { const max = Math.max(1, ...rows.map((row) => row.value)); return <article className="chart-card"><h3>{title}</h3><div className="simple-bars">{rows.length ? rows.map((row) => <div key={row.label}><span>{row.label}</span><div><i style={{ width: `${row.value / max * 100}%` }}/></div><strong>{row.value}</strong>{row.detail && <small>{row.detail}</small>}</div>) : <p className="empty-inline">Sin datos.</p>}</div></article>; }

function alertDueStatus(alert: CommitmentAlert): CommitmentDueStatus {
  if (alert.diasParaVencer < 0) return "Vencido";
  if (alert.diasParaVencer === 0) return "Vence hoy";
  if (alert.diasParaVencer <= 3) return "Próximo a vencer";
  return "En plazo";
}

function CommitmentAlertsPanel({ data, loading, error, onClose, onOpen, onMarkAll }: { data: CommitmentAlertsResponse; loading: boolean; error: string; onClose: () => void; onOpen: (alert: CommitmentAlert) => void; onMarkAll: () => void }) {
  const groups: Array<[string, (alert: CommitmentAlert) => boolean]> = [
    ["Vencidos", (alert) => alert.diasParaVencer < 0],
    ["Vence hoy", (alert) => alert.diasParaVencer === 0],
    ["Próximos", (alert) => alert.diasParaVencer > 0],
  ];
  return <div className="commitment-alert-scrim" onMouseDown={(event) => { if (event.target === event.currentTarget) onClose(); }}>
    <aside className="commitment-alert-panel" aria-label="Centro de alertas de compromisos">
      <header><div><Bell size={20}/><h2>Alertas de compromisos</h2></div><button type="button" className="icon-button" aria-label="Cerrar alertas" onClick={onClose}><X size={20}/></button></header>
      <div className="alert-panel-actions"><span>{data.noLeidas} no leídas</span><button type="button" className="button ghost small" disabled={loading || data.noLeidas === 0} onClick={onMarkAll}>Marcar todas como leídas</button></div>
      {error && <ErrorNotice message={error}/>}
      {loading ? <Loading/> : !data.items.length ? <p className="empty-inline">No tienes alertas pendientes.</p> : <div className="alert-groups">{groups.map(([label, predicate]) => {
        const alerts = data.items.filter(predicate);
        return alerts.length ? <section key={label}><h3>{label}<span>{alerts.length}</span></h3>{alerts.map((alert) => {
          const due = alertDueStatus(alert);
          return <button type="button" className={`commitment-alert-card${alert.leidaAt ? "" : " unread"}`} key={alert.id} onClick={() => onOpen(alert)}><div><strong>{alert.titulo}</strong><DueBadge status={due}/></div><span>{alert.folio || "Sin folio"} · {formatCivilDate(alert.fechaCompromiso)}</span><p>{alert.punto || "Sin descripción"}</p><small>{alert.area || "—"} · {alert.frente || "Todos los frentes"}</small><small>{alert.mensaje}</small></button>;
        })}</section> : null;
      })}</div>}
    </aside>
  </div>;
}
type DashboardFilters = { start: string; end: string; typeId: string; frontIds: string[] };
const dashboardEmpty: DashboardFilters = { start: "", end: "", typeId: "", frontIds: [] };
const chartColors = ["#24745f", "#4b91c7", "#d89b3c", "#8a65ad", "#cf665d", "#65a56f", "#64748b"];
const dashboardStatusOrder: readonly CommunicationDashboardStatus[] = ["Abierto", "En atención", "Cerrado"];
const statusColors: Record<CommunicationDashboardStatus, string> = {
  Abierto: chartColors[1],
  "En atención": chartColors[2],
  Cerrado: chartColors[0],
};
function statusColor(status: string, index: number) {
  const normalized = dashboardStatusOrder.find((item) => item.toLocaleLowerCase("es") === status.trim().toLocaleLowerCase("es"));
  return normalized ? statusColors[normalized] : chartColors[index % chartColors.length];
}
function statusPosition(status: CommunicationDashboardStatus) {
  return dashboardStatusOrder.indexOf(status);
}

function CommunicationsDashboard({ context, types, fronts }: { context: WorkContext; types: JsonRecord[]; fronts: JsonRecord[] }) {
  const [filters, setFilters] = useState(dashboardEmpty);
  const [data, setData] = useState<CommunicationDashboard | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  useEffect(() => {
    let current = true;
    setLoading(true); setData(null); setError("");
    communicationStatistics(context, filters).then((result) => { if (current) setData(result); }).catch((caught) => { if (current) setError(getErrorMessage(caught)); }).finally(() => { if (current) setLoading(false); });
    return () => { current = false; };
  }, [context, filters.start, filters.end, filters.typeId, filters.frontIds]);
  const set = <K extends keyof DashboardFilters>(key: K, value: DashboardFilters[K]) => setFilters((current) => ({ ...current, [key]: value }));
  const frontScale = scaleFromTotals(data?.porFrente ?? []);
  const subfrontScale = scaleFromTotals(data?.porSubfrente ?? []);
  return <section className="dashboard-stack">
    <section className="panel dashboard-filters"><label><span className="field-label">Fecha inicial</span><ExecutionCivilDateInput value={filters.start} max={filters.end || undefined} onChange={(value) => set("start", value)}/></label><label><span className="field-label">Fecha final</span><ExecutionCivilDateInput value={filters.end} min={filters.start || undefined} onChange={(value) => set("end", value)}/></label><label><span className="field-label">Tipo de comunicación</span><select value={filters.typeId} onChange={(event) => set("typeId", event.target.value)}><option value="">Todos los tipos</option>{types.map((row) => <option key={rowValue(row)} value={rowValue(row)}>{rowLabel(row)}</option>)}</select></label><div className="form-field"><span className="field-label">Frente</span><MultiSelect className="dashboard-front-multiselect" values={filters.frontIds} options={catalogOptions(fronts)} onChange={(values) => set("frontIds", values)} placeholder="Seleccione uno o varios" searchPlaceholder="Filtrar..." /></div><button className="button ghost" onClick={() => setFilters(dashboardEmpty)}>Limpiar</button></section>
    {error && <ErrorNotice message={`No fue posible cargar el Dashboard: ${error}`} />}
    {loading ? <section className="panel dashboard-loading"><Loading /></section> : data && data.totalFiltrado === 0 ? <section className="panel empty-dashboard">No hay comunicaciones para los filtros seleccionados.</section> : data && <>
      <div className="kpi-grid"><Kpi title="Total de comunicaciones" value={data.kpis.totalComunicaciones}/><Kpi title="Comunicaciones abiertas" value={data.kpis.comunicacionesAbiertas.total} detail={`Abierto + En atención · ${data.kpis.comunicacionesAbiertas.porcentaje} % del total`}/><Kpi title="Abiertas y sin seguimiento" value={data.kpis.abiertasSinSeguimiento.total} detail={`${data.kpis.abiertasSinSeguimiento.porcentaje} % del total`}/><AttentionKpi value={data.kpis.focoAtencion}/></div>
      <div className="chart-grid"><DonutCard title="Distribución por tipo" rows={data.porTipo}/><DonutCard title="Distribución por estatus" rows={[...data.porEstatus].sort((left, right) => statusPosition(left.etiqueta) - statusPosition(right.etiqueta))} colorFor={statusColor}/></div>
      <div className="chart-grid"><StackedBarCard title="Comunicaciones por frente — Tipo" groupLabel="Frente" categoryLabel="Tipo" rows={data.porFrenteTipo.map((item) => ({ group: item.frente, category: item.tipo, total: item.total, porcentaje: item.porcentaje }))} {...frontScale}/><StackedBarCard title="Comunicaciones por frente — Estatus" groupLabel="Frente" categoryLabel="Estatus" rows={[...data.porFrenteEstatus].sort((left, right) => statusPosition(left.estatus) - statusPosition(right.estatus)).map((item) => ({ group: item.etiqueta, category: item.estatus, total: item.total, porcentaje: item.porcentaje }))} colorFor={statusColor} {...frontScale}/></div>
      {filters.frontIds.length === 1 && data.frenteSeleccionado ? <section><div className="dashboard-section-title"><h2>Análisis por subfrente</h2><p>{data.frenteSeleccionado.etiqueta}. Las cantidades representan asociaciones comunicación-subfrente y pueden superar el total único.</p></div><div className="chart-grid"><StackedBarCard title="Comunicaciones por subfrente — Tipo" groupLabel="Subfrente" categoryLabel="Tipo" rows={data.porSubfrenteTipo.map((item) => ({ group: item.etiqueta, category: item.tipo, total: item.total, porcentaje: item.porcentaje }))} {...subfrontScale}/><StackedBarCard title="Comunicaciones por subfrente — Estatus" groupLabel="Subfrente" categoryLabel="Estatus" rows={[...data.porSubfrenteEstatus].sort((left, right) => statusPosition(left.estatus) - statusPosition(right.estatus)).map((item) => ({ group: item.etiqueta, category: item.estatus, total: item.total, porcentaje: item.porcentaje }))} colorFor={statusColor} {...subfrontScale}/></div></section> : <div className="subfront-hint">Selecciona un solo frente para visualizar el desglose por subfrente.</div>}
    </>}
  </section>;
}

function Kpi({ title, value, detail }: { title: string; value: string | number; detail?: string }) { return <article className="kpi-card"><span>{title}</span><strong>{value}</strong>{detail && <small>{detail}</small>}</article>; }
function AttentionKpi({ value }: { value: CommunicationDashboard["kpis"]["focoAtencion"] }) {
  return <article className="kpi-card"><span>Foco de atención</span><strong>{value?.etiqueta || "Sin pendientes"}</strong>{value && <small>{value.nivel === "subfrente" ? "Subfrente" : "Frente"} · {value.total} abiertas sin seguimiento</small>}</article>;
}
type PointDueStatus = "Sin fecha" | "Cerrado" | "Vencido" | "Vence hoy" | "En plazo";

export function communicationPointDueStatus(point: Pick<CommunicationPoint, "fecha_compromiso" | "estatus">, today = localToday()): PointDueStatus {
  const commitmentDate = String(point.fecha_compromiso || "").match(/^\d{4}-\d{2}-\d{2}/)?.[0] || "";
  if (!commitmentDate) return "Sin fecha";
  if (point.estatus === "Cerrado") return "Cerrado";
  if (commitmentDate < today) return "Vencido";
  if (commitmentDate === today) return "Vence hoy";
  return "En plazo";
}

function FollowUpRelationsList({ relations, onPdf, onNavigate }: { relations: CommunicationFollowUp[]; onPdf: (row: JsonRecord) => void; onNavigate?: (row: JsonRecord, pointId?: string) => void }) {
  if (!relations.length) return <p className="followup-context-empty">Sin seguimientos registrados.</p>;
  return <div className="followup-context-list">
    {relations.map((row, index) => {
      const viaCommunication = row.modalidad !== "otro_medio" && Boolean(row.doc_hijo_id);
      return <article className="followup-context-card" key={String(row.id || `${row.doc_hijo_id || "medio"}-${index}`)}>
        <div><span>Tipo de seguimiento</span><strong>{row.tipo || "—"}</strong></div>
        <div><span>Fecha</span><strong>{row.fecha ? formatCivilDate(row.fecha) : "—"}</strong></div>
        <div><span>{viaCommunication ? "Folio" : "Medio"}</span><strong>{viaCommunication ? row.folio || "—" : "Otro medio"}</strong></div>
        {row.punto_hijo && <div className="followup-context-target"><span>Punto destino</span><strong>{row.punto_hijo}</strong></div>}
        <div className="followup-context-summary"><span>Resumen</span><p>{row.resumen || "—"}</p></div>
        {viaCommunication && onNavigate && <button type="button" className="button-link" onClick={() => onNavigate({ id: row.doc_hijo_id, folio: row.folio }, row.punto_hijo_id || undefined)}>Abrir seguimiento →</button>}
        {viaCommunication && <button type="button" className="icon-button followup-context-pdf" title="Ver PDF" aria-label="Ver PDF del seguimiento" onClick={() => onPdf({ id: row.doc_hijo_id, folio: row.folio })}><Eye size={17} /></button>}
      </article>;
    })}
  </div>;
}

function FollowUpComposer({
  catalog,
  parent,
  pointId,
  onCreateNew,
  onCancel,
  onPdf,
  onSaved,
}: {
  catalog: JsonRecord;
  parent: JsonRecord;
  pointId: string | null;
  onCreateNew: (pointId: string | null) => void;
  onCancel: () => void;
  onPdf: (row: JsonRecord) => void;
  onSaved: () => Promise<void>;
}) {
  const { context, permissions } = useSession();
  const isAdmin = String(context?.rol || "").toLocaleLowerCase("es") === "admin";
  const assignedPermission = permissions.find((permission) =>
    permission.proyecto_id === context?.proyecto_id
    && permission.contrato_id === context?.contrato_id
    && permission.area_id,
  );
  const assignedAreaId = context?.area_id || assignedPermission?.area_id || "";
  const assignedAreaName = context?.area_nombre || assignedPermission?.area_nombre || "Área asignada";
  type FollowUpMode = "communication" | "other";
  const parentIsMinute = isMinuteDocument(parent.tipo_doc || parent.tipo_documento || parent.tipo);
  const [mode, setMode] = useState<FollowUpMode>("communication");
  const [targetDetail, setTargetDetail] = useState<CommunicationDetail | null>(null);
  const [targetDetailLoading, setTargetDetailLoading] = useState(false);
  const [targetPointId, setTargetPointId] = useState("");
  const recipients = (parent.destinatarios as CommunicationRecipient[] | undefined) || [];
  const [recipientId, setRecipientId] = useState(recipients.length === 1 ? recipients[0].id || "" : "");
  const [clase, setClase] = useState<"Seguimiento" | "Respuesta" | "Cierre">("Seguimiento");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [values, setValues] = useState<JsonRecord>({ area_id: isAdmin ? "" : assignedAreaId, doc_hijo_id: "", tipo_id: "", fecha: "", resumen: "" });
  const parentDate = String(parent.fecha || "").slice(0, 10);
  const documents = arrayFrom(catalog, ["documentos"]).filter((row) => {
    const id = String(row.id || "");
    const date = String(row.fecha || "").slice(0, 10);
    return id !== String(parent.id) && Boolean(parentDate) && Boolean(date) && date > parentDate;
  });
  const selectedDocument = documents.find((row) => String(row.id) === String(values.doc_hijo_id));
  const types = arrayFrom(catalog, ["tiposSeguimiento", "tipos_seguimiento"]);
  const areas = arrayFrom(catalog, ["areas"]);
  const selectedDocumentAreaId = String(selectedDocument?.areaId || selectedDocument?.area_id || "");
  const selectedDocumentArea = areas.find((area) => rowValue(area) === selectedDocumentAreaId);
  const targetPoints = targetDetail?.puntos || [];
  const selectedTargetPoint = targetPoints.find((point) => String(point.id) === targetPointId);
  function set(key: string, value: unknown) { setValues((current) => ({ ...current, [key]: value })); }

  function changeMode(nextMode: FollowUpMode) {
    setMode(nextMode);
    setError("");
    setTargetPointId("");
    setTargetDetail(null);
    setValues((current) => ({
      ...current,
      area_id: isAdmin ? "" : assignedAreaId,
      doc_hijo_id: "",
      tipo_id: "",
      fecha: "",
      resumen: "",
    }));
  }

  useEffect(() => {
    const documentId = String(values.doc_hijo_id || "");
    if (mode !== "communication" || !pointId || !documentId) {
      setTargetDetail(null);
      setTargetPointId("");
      setTargetDetailLoading(false);
      return;
    }
    let current = true;
    setTargetDetailLoading(true);
    setTargetPointId("");
    getCommunication(documentId)
      .then((detail) => { if (current) setTargetDetail(detail); })
      .catch((caught) => { if (current) setError(getErrorMessage(caught)); })
      .finally(() => { if (current) setTargetDetailLoading(false); });
    return () => { current = false; };
  }, [mode, pointId, values.doc_hijo_id]);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError("");
    try {
      if (mode === "communication" && !selectedDocument) throw new Error("Seleccione la comunicación que desea relacionar.");
      if (parent.requiere_respuesta && recipients.length && !recipientId) throw new Error("Seleccione el destinatario al que atiende.");
      const summary = mode === "communication" ? String(selectedDocument?.resumen || "") || null : String(values.resumen || "").trim();
      await createCommunicationFollowUp({
        area_id: mode === "communication" ? selectedDocumentAreaId || null : String(isAdmin ? values.area_id : assignedAreaId) || null,
        doc_padre_id: String(parent.id),
        doc_hijo_id: mode === "communication" ? String(selectedDocument?.id || "") : null,
        punto_doc_id: pointId,
        punto_hijo_id: pointId && mode === "communication" ? targetPointId || null : null,
        destinatario_id: recipientId || null, clase,
        tipo_id: mode === "communication" ? clase : String(values.tipo_id || ""),
        fecha: mode === "communication" ? String(selectedDocument?.fecha || "") || null : String(values.fecha || "") || null,
        resumen: summary,
      });
      await onSaved();
      onCancel();
    } catch (caught) {
      setError(getErrorMessage(caught));
    } finally {
      setBusy(false);
    }
  }

  return <form className="form-grid followup-form followup-inline-form" onSubmit={submit}>
    {error && <div className="notice error full">{error}</div>}
    <div className="followup-mode full" role="tablist" aria-label="Modalidad de seguimiento">
      <button type="button" role="tab" aria-selected={mode === "communication"} className={mode === "communication" ? "active" : ""} onClick={() => changeMode("communication")}>Mediante una comunicación</button>
      <button type="button" role="tab" aria-selected={mode === "other"} className={mode === "other" ? "active" : ""} onClick={() => changeMode("other")}>Por otro medio</button>
    </div>

    {mode === "communication" && <>
      <div className="full followup-section-heading">
        <div><strong>{pointId ? "Seguimiento del punto" : "Seguimiento general"}</strong><span>Sólo se muestran comunicaciones con fecha posterior a la comunicación origen.</span></div>
        {parentIsMinute
          ? <button type="button" className="button secondary small" onClick={() => onCreateNew(pointId)}><FilePlus2 size={16} /> Registrar nueva comunicación</button>
          : <small>Para Oficios y Notas, relacione una comunicación existente con el destinatario correspondiente.</small>}
      </div>
      <label className="full">Comunicación relacionada<select value={String(values.doc_hijo_id)} onChange={(event) => { setValues((current) => ({ ...current, doc_hijo_id: event.target.value })); setTargetPointId(""); }} required><option value="">Selecciona un folio posterior…</option>{documents.map((row) => <option key={rowValue(row)} value={rowValue(row)}>{`${String(row.folio || "")} — ${formatCivilDate(row.fecha)}`}</option>)}</select></label>
      {pointId && selectedDocument && <div className="form-field full followup-target-point">
        <span className="field-label">Punto de la comunicación de seguimiento</span>
        {targetDetailLoading ? <Loading /> : targetPoints.length ? <>
          <select value={targetPointId} onChange={(event) => setTargetPointId(event.target.value)}>
            <option value="">Seguimiento a la comunicación completa</option>
            {targetPoints.map((point, index) => <option key={point.id} value={point.id}>{`Punto ${index + 1} — ${point.punto.slice(0, 90)}`}</option>)}
          </select>
          {selectedTargetPoint && <div className="followup-selected-point"><span>Punto destino</span><strong>{selectedTargetPoint.punto || "—"}</strong></div>}
        </> : <div className="notice info">La comunicación seleccionada no tiene puntos registrados; el seguimiento será a la comunicación completa.</div>}
      </div>}
      <div className="form-field readonly-area"><span className="field-label">Área</span><strong>{selectedDocument ? rowLabel(selectedDocumentArea || {}) || "—" : "—"}</strong></div>
      <div className="form-field readonly-area"><span className="field-label">Fecha</span><strong>{selectedDocument ? formatCivilDate(selectedDocument.fecha) : "—"}</strong></div>
      <label>Clase<select value={clase} onChange={(event) => setClase(event.target.value as typeof clase)}>{["Seguimiento", "Respuesta", "Cierre"].map((value) => <option key={value}>{value}</option>)}</select></label>
      {Boolean(parent.requiere_respuesta) && recipients.length > 1 && <label>Destinatario al que atiende<select required value={recipientId} onChange={(event) => setRecipientId(event.target.value)}><option value="">Seleccione…</option>{recipients.map((recipient) => <option key={recipient.id} value={recipient.id}>{recipient.tipo_destinatario === "Otro" ? recipient.nombre_otro : recipient.tipo_destinatario}</option>)}</select></label>}
      <label className="full">Resumen<textarea rows={3} value={String(selectedDocument?.resumen || "")} readOnly placeholder="El resumen de la comunicación seleccionada aparecerá aquí." /></label>
    </>}

    {mode === "other" && <>
      <div className="full followup-section-heading"><div><strong>{pointId ? "Seguimiento del punto por otro medio" : "Seguimiento general por otro medio"}</strong><span>No se creará una comunicación ni se solicitará un PDF.</span></div></div>
      <label>Tipo de seguimiento<select value={String(values.tipo_id)} onChange={(event) => set("tipo_id", event.target.value)} required><option value="">Selecciona…</option>{types.map((row) => <option key={rowValue(row)} value={rowValue(row)}>{rowLabel(row)}</option>)}</select></label>
      <label>Fecha<ExecutionCivilDateInput min={nextCivilDate(parent.fecha) || undefined} max={localToday()} value={String(values.fecha)} onChange={(value) => set("fecha", value)} required /></label>
      {isAdmin ? <label>Área<select value={String(values.area_id)} onChange={(event) => set("area_id", event.target.value)} required><option value="">Selecciona…</option>{areas.map((row) => <option key={rowValue(row)} value={rowValue(row)}>{rowLabel(row)}</option>)}</select></label> : <div className="form-field readonly-area"><span className="field-label">Área</span><strong>{assignedAreaName}</strong></div>}
      <label className="full">Resumen<textarea rows={4} value={String(values.resumen)} onChange={(event) => set("resumen", event.target.value)} required placeholder="Describa el seguimiento realizado…" /></label>
    </>}

    <div className="modal-actions full">
      <button type="button" className="button ghost" disabled={busy} onClick={onCancel}>Cancelar</button>
      <button className="button primary" disabled={busy || targetDetailLoading || (mode === "communication" && !selectedDocument)}>{busy ? "Guardando…" : "Crear seguimiento"}</button>
    </div>
  </form>;
}

function CommunicationPointsModal({
  state,
  catalog,
  followUps,
  onClose,
  onCreateNew,
  onPdf,
  onSaved,
  onNavigate,
}: {
  state: { parent: JsonRecord; folio: string; detail: CommunicationDetail | null; loading: boolean; error: string; initialPointId?: string; initialRecipientId?: string };
  catalog: JsonRecord;
  followUps: CommunicationFollowUp[];
  onClose: () => void;
  onCreateNew: (pointId: string | null) => void;
  onPdf: (row: JsonRecord) => void;
  onSaved: () => Promise<void>;
  onNavigate: (row: JsonRecord, pointId?: string) => void;
}) {
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set(["general"]));
  const [composerKey, setComposerKey] = useState<string | null>(null);
  const targetPointRef = useRef<HTMLElement | null>(null);
  const detail = state.detail;
  const points = detail?.destinatarios?.length ? [] : detail?.puntos || [];
  const communicationId = String(detail?.id || state.parent.id || "");
  const activeRelations = followUps.filter((row) => row.deleted_at == null && String(row.doc_padre_id || "") === communicationId);
  const generalRelations = activeRelations.filter((row) => !row.punto_doc_id);

  useEffect(() => {
    if (state.loading || !state.initialPointId) return;
    setExpanded((current) => new Set(current).add(state.initialPointId as string));
    requestAnimationFrame(() => targetPointRef.current?.scrollIntoView({ behavior: "smooth", block: "center" }));
  }, [state.loading, state.initialPointId, detail?.id]);

  function toggleSection(key: string) {
    setExpanded((current) => {
      const next = new Set(current);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  function toggleComposer(key: string) {
    setComposerKey((current) => current === key ? null : key);
    setExpanded((current) => new Set(current).add(key));
  }

  return <Modal title={`Puntos y seguimiento de la comunicación ${state.folio}`} className="modal-wide communication-followups-modal" onClose={onClose}>
    <div className="communication-points-view">
      {state.loading ? <Loading /> : state.error ? <ErrorNotice message={state.error} /> : detail ? <>
        <section className="communication-points-summary followup-context-section">
          <span>Resumen</span>
          <p>{detail.resumen || "—"}</p>
          <div className="followup-context-toolbar">
            <button type="button" className="button-link followup-list-toggle" aria-expanded={expanded.has("general")} onClick={() => toggleSection("general")}><ChevronDown size={16} className={expanded.has("general") ? "expanded" : ""} /> Seguimientos generales ({generalRelations.length})</button>
            <button type="button" className="button secondary small" onClick={() => toggleComposer("general")}><GitBranch size={15} /> {composerKey === "general" ? "Cerrar alta" : "Agregar seguimiento general"}</button>
          </div>
          {expanded.has("general") && <FollowUpRelationsList relations={generalRelations} onPdf={onPdf} onNavigate={onNavigate} />}
          {composerKey === "general" && <FollowUpComposer catalog={catalog} parent={state.parent} pointId={null} onCreateNew={onCreateNew} onCancel={() => setComposerKey(null)} onPdf={onPdf} onSaved={onSaved} />}
        </section>

        {!!detail.destinatarios?.length && <section className="recipient-details">{detail.destinatarios.map((recipient, index) => <article tabIndex={-1} ref={(node) => { if (node && recipient.id === state.initialRecipientId) node.scrollIntoView({ block: "center" }); }} className={`communication-point-readonly${recipient.id === state.initialRecipientId ? " highlighted" : ""}`} key={recipient.id || index}>
          <strong>{recipient.tipo_destinatario === "Otro" ? recipient.nombre_otro : recipient.tipo_destinatario}</strong>
          <div>Acuse: {recipient.fecha_recepcion ? formatCivilDate(recipient.fecha_recepcion) : "Pendiente"}</div>
          <div>Vencimiento: {recipient.fecha_vencimiento ? formatCivilDate(recipient.fecha_vencimiento) : recipient.fecha_recepcion ? "Pendiente" : "Pendiente de acuse"}</div>
          <div>Estado: {recipient.atendido ? "Atendido" : detail?.requiere_respuesta ? "Pendiente" : "No requiere respuesta"}</div>
        </article>)}</section>}
        {points.length ? <div className="communication-points-readonly-list">
          {points.map((point, index) => {
            const pointKey = String(point.id || `point-${index}`);
            const dueStatus = communicationPointDueStatus(point);
            const pointRelations = activeRelations.filter((row) => String(row.punto_doc_id || "") === String(point.id));
            const targeted = String(point.id || "") === String(state.initialPointId || "");
            return <article className={`communication-point-readonly followup-context-section${targeted ? " highlighted" : ""}`} ref={targeted ? (node) => { targetPointRef.current = node; } : undefined} key={pointKey}>
              <div className="readonly-point-text"><span>Punto {index + 1}</span><strong>{point.punto || "—"}</strong></div>
              <div className="communication-point-readonly-meta">
                <div><span>Fecha de compromiso</span><strong>{point.fecha_compromiso ? formatCivilDate(point.fecha_compromiso) : "—"}</strong></div>
                <div><span>Responsables</span><strong>{point.responsables?.length ? point.responsables.join(", ") : "—"}</strong></div>
                <div><span>Estatus</span><strong className={`point-status ${point.estatus.toLocaleLowerCase("es")}`}>{point.estatus}</strong></div>
                <div><span>Vencimiento</span><strong className={`due-status due-${dueStatus.toLocaleLowerCase("es").replace(/\s+/g, "-")}`}>{dueStatus}</strong></div>
              </div>
              {!!point.historial_fechas?.length && <section><strong>Historial de fechas</strong>{point.historial_fechas.map((entry) => <div className="date-history-entry" key={entry.id}><div>{formatCivilDate(entry.fecha_anterior)} → {formatCivilDate(entry.fecha_nueva)}</div><div>Reprogramada: {formatCivilDate(entry.fecha_reprogramacion)}</div><div>Motivo: {entry.motivo}</div></div>)}</section>}
              <div className="followup-context-toolbar">
                <button type="button" className="button-link followup-list-toggle" aria-expanded={expanded.has(pointKey)} onClick={() => toggleSection(pointKey)}><ChevronDown size={16} className={expanded.has(pointKey) ? "expanded" : ""} /> Seguimientos ({pointRelations.length})</button>
                <button type="button" className="button secondary small" onClick={() => toggleComposer(pointKey)}><GitBranch size={15} /> {composerKey === pointKey ? "Cerrar alta" : "Agregar seguimiento"}</button>
              </div>
              {expanded.has(pointKey) && <FollowUpRelationsList relations={pointRelations} onPdf={onPdf} onNavigate={onNavigate} />}
              {composerKey === pointKey && <FollowUpComposer catalog={catalog} parent={state.parent} pointId={String(point.id)} onCreateNew={onCreateNew} onCancel={() => setComposerKey(null)} onPdf={onPdf} onSaved={onSaved} />}
            </article>;
          })}
        </div> : !detail.destinatarios?.length ? <p className="empty-inline">No hay puntos registrados para esta comunicación.</p> : null}
      </> : null}
    </div>
  </Modal>;
}
function DonutCard({ title, rows, colorFor = (_, index) => chartColors[index % chartColors.length] }: { title: string; rows: Array<{ etiqueta: string; total: number; porcentaje: number }>; colorFor?: (label: string, index: number) => string }) {
  let position = 0; const stops = rows.map((row, index) => { const start = position; position += Number(row.porcentaje) || 0; return `${colorFor(row.etiqueta, index)} ${start}% ${position}%`; });
  return <article className="chart-card"><h3>{title}</h3><div className="donut-layout"><div className="donut" style={{ background: `conic-gradient(${stops.join(",")})` }} aria-label={title}/><div className="chart-legend">{rows.map((row, index) => <div key={`${row.etiqueta}-${index}`} title={`${row.etiqueta}: ${row.total}, ${row.porcentaje} %`}><i style={{ background: colorFor(row.etiqueta, index) }}/><span>{row.etiqueta}</span><strong>{row.total} · {row.porcentaje} %</strong></div>)}</div></div></article>;
}
type StackedRow = { group: string; category: string; total: number; porcentaje: number };
function scaleFromTotals(rows: Array<{ etiqueta: string; total: number }>) {
  const ordered = [...rows].sort((left, right) => right.total - left.total);
  return { groupOrder: ordered.map((row) => row.etiqueta), maxTotal: Math.max(0, ...ordered.map((row) => row.total)) };
}
function StackedBarCard({ title, groupLabel, categoryLabel, rows, groupOrder, maxTotal, colorFor = (_, index) => chartColors[index % chartColors.length] }: { title: string; groupLabel: string; categoryLabel: string; rows: StackedRow[]; groupOrder: string[]; maxTotal: number; colorFor?: (label: string, index: number) => string }) {
  const categories = [...new Set(rows.map((row) => row.category))];
  const groups = groupOrder.filter((group) => rows.some((row) => row.group === group));
  return <article className="chart-card chart-wide"><h3>{title}</h3><div className="stacked-chart">{groups.map((group) => {
    const groupRows = rows.filter((row) => row.group === group);
    const groupTotal = groupRows.reduce((sum, row) => sum + row.total, 0);
    return <div className="stacked-row" key={group}><span>{group}</span><div>{groupRows.map((row) => <i key={row.category} style={{ width: `${maxTotal ? row.total / maxTotal * 100 : 0}%`, background: colorFor(row.category, categories.indexOf(row.category)) }} title={`${groupLabel}: ${group}\n${categoryLabel}: ${row.category}\nCantidad: ${row.total} comunicaciones\nPorcentaje: ${row.porcentaje} % del ${groupLabel.toLocaleLowerCase("es")}\nTotal: ${groupTotal} comunicaciones`} aria-label={`${group}, ${row.category}: ${row.total} comunicaciones, ${row.porcentaje} %, total ${groupTotal}`}/>)}</div><strong>{groupTotal}</strong></div>;
  })}</div><div className="compact-legend">{categories.map((category, index) => <span key={category}><i style={{ background: colorFor(category, index) }}/>{category}</span>)}</div></article>;
}

function RowActions({ row, busy, onEdit, onPoints, onDelete, onPdf }: { row: JsonRecord; busy: boolean; onEdit: () => void; onPoints: () => void; onDelete: () => void; onPdf: () => void }) {
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const root = useRef<HTMLDivElement>(null);
  useEffect(() => {
    function close(event: MouseEvent) { if (!root.current?.contains(event.target as Node)) setOpen(false); }
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);
  function run(action: () => void) { setOpen(false); action(); }
  function toggleMenu() {
    if (!open && root.current) {
      const rect = root.current.getBoundingClientRect();
      const menuHeight = 202;
      const top = rect.bottom + menuHeight > window.innerHeight ? rect.top - menuHeight - 5 : rect.bottom + 5;
      setPosition({ top: Math.max(8, top), left: Math.max(8, rect.right - 170) });
    }
    setOpen((value) => !value);
  }
  return <div className="row-menu" ref={root}>
    <button className="icon-button" aria-label={`Acciones de ${String(row.folio || "comunicación")}`} aria-expanded={open} onClick={toggleMenu}><MoreVertical size={19} /></button>
    {open && <div className="row-menu-popover" style={position}>
      <button disabled={busy || row.puede_editar === false} onClick={() => run(onEdit)}><Pencil size={16} /> Editar</button>
      <button disabled={busy} onClick={() => run(onPoints)}><ListChecks size={16} /> Puntos y seguimiento</button>
      <button disabled={busy || row.tiene_archivo !== true} onClick={() => run(onPdf)}><Eye size={16} /> Ver PDF</button>
      <button className="danger" disabled={busy} onClick={() => run(onDelete)}><Trash2 size={16} /> Eliminar</button>
    </div>}
  </div>;
}

function emptyCommunicationPoint(): CommunicationPointDraft {
  return {
    clasificacion: "Apertura de acuerdo", requiere_seguimiento: true, antecedente_doc_id: null, antecedente_punto_id: null,
    clientId: crypto.randomUUID(),
    punto: "",
    tipo_asunto_id: "",
    responsables: [],
    fecha_compromiso: "",
    estatus: "Abierto",
  };
}

function communicationPointDraft(point: CommunicationPoint): CommunicationPointDraft {
  const id = String(point.id || "");
  return {
    ...(id ? { id } : {}),
    clasificacion: point.clasificacion || "Apertura de acuerdo", requiere_seguimiento: point.requiere_seguimiento ?? Boolean(point.fecha_compromiso), antecedente_doc_id: point.antecedente_doc_id || null, antecedente_punto_id: point.antecedente_punto_id || null,
    clientId: id || crypto.randomUUID(),
    punto: String(point.punto || ""),
    tipo_asunto_id: String(point.tipo_asunto_id || ""),
    responsables: Array.isArray(point.responsables) ? point.responsables.filter((party): party is CommunicationPartyPreset => COMMUNICATION_PARTIES.includes(party)) : [],
    fecha_compromiso: String(point.fecha_compromiso || "").slice(0, 10),
    estatus: POINT_STATUSES.includes(point.estatus) ? point.estatus : "Abierto",
  };
}

function partySelector(value: unknown, fallback: CommunicationPartySelector = ""): CommunicationPartySelector {
  const party = String(value || "").trim();
  if (COMMUNICATION_PARTIES.includes(party as CommunicationPartyPreset)) return party as CommunicationPartyPreset;
  return party ? "Otro" : fallback;
}

function CommunicationModal({ catalog, communication, followUpParent, followUpPointId = null, onClose, onSaved }: { catalog: JsonRecord; communication?: CommunicationDetail; followUpParent?: JsonRecord; followUpPointId?: string | null; onClose: () => void; onSaved: () => Promise<void> }) {
  const { context } = useSession();
  const followUpParentIsMinute = Boolean(followUpParent && isMinuteDocument(followUpParent.tipo_doc || followUpParent.tipo_documento || followUpParent.tipo));
  const initialNewPoint = emptyCommunicationPoint();
  if (followUpParentIsMinute && followUpParent) {
    initialNewPoint.clasificacion = "Seguimiento";
    initialNewPoint.requiere_seguimiento = true;
    initialNewPoint.antecedente_doc_id = String(followUpParent.id || "") || null;
    initialNewPoint.antecedente_punto_id = followUpPointId || null;
  }
  const isAdmin = String(context?.rol || "").toLocaleLowerCase("es") === "admin";
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [followUpTypeId, setFollowUpTypeId] = useState("");
  const [followUpTargetPointClientId, setFollowUpTargetPointClientId] = useState(() => followUpParentIsMinute ? initialNewPoint.clientId : "");
  const [emisorSelector, setEmisorSelector] = useState<CommunicationPartySelector>(() => partySelector(communication?.emisor, "Supervisión"));
  const [nodes, setNodes] = useState<FollowUpNode[]>([]);
  const [nodesError, setNodesError] = useState("");
  const [antecedentScopeSelected, setAntecedentScopeSelected] = useState<Record<string, boolean>>(() => followUpParentIsMinute ? { [initialNewPoint.clientId]: true } : {});
  const initialParticipants = minuteParticipantSelection(communication?.participantes || []);
  const [participantChoices, setParticipantChoices] = useState<string[]>(initialParticipants.choices);
  const [participantCustom, setParticipantCustom] = useState(initialParticipants.custom);
  const [personnel, setPersonnel] = useState<CommunicationAreaPerson[]>([]);
  const [personnelLoading, setPersonnelLoading] = useState(false);
  const [personnelError, setPersonnelError] = useState("");
  const [values, setValues] = useState<CommunicationFormValues>({
    destinatarios: communication?.destinatarios?.length ? communication.destinatarios : [{ tipo_destinatario: (partySelector(communication?.destinatario, "Contratista") || "Contratista"), nombre_otro: partySelector(communication?.destinatario) === "Otro" ? communication?.destinatario || null : null, fecha_recepcion: null }],
    requiere_respuesta: communication?.requiere_respuesta ?? false,
    dias_contestacion: communication?.dias_contestacion ?? null,
    titulo: communication?.titulo || "",
    autor_user_id: communication?.autor_user_id || null,
    autor_nombre: communication?.autor_nombre || null,
    participantes: communication?.participantes || [],
    fecha: String(communication?.fecha || localToday()).slice(0, 10),
    tipo_doc_id: String(communication?.tipo_doc_id || ""),
    folio: String(communication?.folio || ""),
    resumen: String(communication?.resumen || ""),
    emisor: String(communication?.emisor || "Supervisión"),
    destinatario: String(communication?.destinatario || ""),
    frente_id: String(communication?.frente_id || ""),
    area_id: String((isAdmin ? communication?.area_id || context?.area_id : context?.area_id) || ""),
    actividad_material_id: stringArray(communication?.actividad_material_id),
    entregable_id: stringArray(communication?.entregable_id),
    subfrente_id: stringArray(communication?.subfrente_id),
    estimaciones_id: stringArray(communication?.estimaciones_id),
    puntos: communication ? (communication.puntos || []).map(communicationPointDraft) : [initialNewPoint],
  });
  const types = arrayFrom(catalog, ["tiposDocumento", "tipos_documento", "tipos_doc", "tipos"]);
  const fronts = arrayFrom(catalog, ["frentes"]);
  const areas = arrayFrom(catalog, ["areas"]);
  const activities = arrayFrom(catalog, ["actividadesMateriales", "actividades_materiales"]);
  const deliverables = ((catalog.entregablesComunicacion as CommunicationDeliverable[] | undefined) || []);
  const historicalMaterialText = activities
    .filter((row) => values.actividad_material_id.includes(rowValue(row)))
    .map((row) => rowLabel(row))
    .join(", ") || "Sin Material / Trabajo";
  const subjects = arrayFrom(catalog, ["tiposAsunto", "tipos_asunto"]);
  const estimations = arrayFrom(catalog, ["estimaciones"]);
  const followUpTypes = arrayFrom(catalog, ["tiposSeguimiento", "tipos_seguimiento"]);
  const subfronts = values.frente_id
    ? arrayFrom(catalog, ["subfrentes"]).filter((row) => String(row.frenteId || row.frente_id || "") === String(values.frente_id))
    : [];
  const selectedType = types.find((row) => rowValue(row) === values.tipo_doc_id);
  const isMinute = isMinuteDocument(selectedType ? rowLabel(selectedType) : "");
  const followUpMinutes = useMemo(() => {
    const grouped = new Map<string, { id: string; folio: string; nodes: FollowUpNode[] }>();
    for (const node of nodes) {
      const current = grouped.get(node.antecedente_doc_id);
      if (current) current.nodes.push(node);
      else grouped.set(node.antecedente_doc_id, { id: node.antecedente_doc_id, folio: node.folio, nodes: [node] });
    }
    return [...grouped.values()];
  }, [nodes]);
  const needsAuthor = isMinute && values.emisor.trim() === "Supervisión";
  const historicalAuthor = Boolean(communication?.autor_user_id && communication.autor_user_id === values.autor_user_id
    && communication.area_id === values.area_id);

  useEffect(() => {
    let active = true;
    setPersonnel([]); setPersonnelError("");
    if (!needsAuthor || !values.area_id || !context) { setPersonnelLoading(false); return; }
    setPersonnelLoading(true);
    listCommunicationAreaPersonnel(context, values.area_id)
      .then((people) => { if (active) setPersonnel(people); })
      .catch((caught) => { if (active) setPersonnelError(getErrorMessage(caught)); })
      .finally(() => { if (active) setPersonnelLoading(false); });
    return () => { active = false; };
  }, [needsAuthor, values.area_id, context?.proyecto_id, context?.contrato_id]);

  useEffect(() => {
    let active = true;
    setNodes([]); setNodesError("");
    if (!context || !isMinute) return;
    availableFollowUpNodes(context, values.fecha, values.area_id).then((rows) => { if (active) setNodes(rows); }).catch((error) => { if (active) setNodesError(getErrorMessage(error)); });
    return () => { active = false; };
  }, [context, isMinute, values.fecha, values.area_id]);

  function set<K extends keyof CommunicationFormValues>(key: K, value: CommunicationFormValues[K]) { setValues((current) => ({ ...current, [key]: value })); }
  function updatePoint(clientId: string, patch: Partial<CommunicationPointDraft>) {
    setValues((current) => ({ ...current, puntos: current.puntos.map((point) => point.clientId === clientId ? { ...point, ...patch, ...(["Informativo", "Cierre"].includes(patch.clasificacion || point.clasificacion) ? { requiere_seguimiento: false, fecha_compromiso: "" } : {}) } : point) }));
  }
  function validateValues() {
    if (!values.fecha) return "La fecha es obligatoria.";
    if (followUpParent && String(followUpParent.fecha || "") && values.fecha <= String(followUpParent.fecha).slice(0, 10)) return "La fecha de la comunicación de seguimiento debe ser posterior a la fecha de la comunicación origen.";
    if (!values.tipo_doc_id) return "El tipo de comunicación es obligatorio.";
    if (!values.folio.trim()) return "El folio es obligatorio.";
    if (!values.titulo.trim()) return "El título es obligatorio.";
    if (values.titulo.trim().length > 500) return "El título no puede exceder 500 caracteres.";
    if (!values.resumen.trim()) return "El resumen es obligatorio.";
    if (!emisorSelector) return "Seleccione un emisor.";
    if (!values.emisor.trim()) return emisorSelector === "Otro" ? "Especifique el emisor." : "Seleccione un emisor.";
    if (values.emisor.trim().length > 150) return "El emisor no puede exceder 150 caracteres.";
    if (emisorSelector === "Otro" && values.emisor.trim() === "Otro") return "Escriba el nombre real de la entidad emisora.";
    if (isMinute) {
      if (needsAuthor) {
        if (!values.autor_user_id) return "Seleccione un autor.";
        if (!historicalAuthor && (personnelLoading || personnelError || !personnel.some((person) => person.user_id === values.autor_user_id))) return "Seleccione personal activo del área como autor.";
      }
      if (!participantChoices.length) return "Seleccione al menos un participante.";
      if (participantChoices.includes("Otro") && !participantCustom.split(";").some((name) => name.trim())) return "La entidad participante es obligatoria.";
      if (participantChoices.includes("Otro") && participantCustom.split(";").some((name) => name.trim() === "Otro")) return "Escriba el nombre real de la entidad participante.";
    } else {
      if (!values.destinatarios.length) return "Agregue al menos un destinatario.";
      if (values.destinatarios.some((r) => r.tipo_destinatario === "Otro" && !r.nombre_otro?.trim())) return "Especifique el nombre del destinatario.";
      if (values.destinatarios.some((r) => r.fecha_recepcion && r.fecha_recepcion < values.fecha)) return "La fecha de acuse de recepción no puede ser anterior a la fecha de la comunicación.";
      if (values.dias_contestacion !== null && (!Number.isInteger(values.dias_contestacion) || Number(values.dias_contestacion) <= 0)) return "Los días para contestación deben ser un entero positivo.";
    }
    const antecedents = values.puntos.filter((p) => isMinute && ["Seguimiento", "Cierre"].includes(p.clasificacion) && p.antecedente_doc_id).map((p) => `${p.antecedente_doc_id}:${p.antecedente_punto_id || ""}`);
    if (new Set(antecedents).size !== antecedents.length) return "Un antecedente sólo puede usarse una vez; no se permiten ramas.";
    if (!values.area_id) return "El área es obligatoria.";
    if (isMinute && !values.puntos.length) return "Agregue al menos un punto.";
    for (const [index, point] of (isMinute ? values.puntos : []).entries()) {
      const label = `Punto ${index + 1}`;
      if (!point.punto.trim()) return `${label} — Escriba la descripción.`;
      if (!point.tipo_asunto_id) return `${label} — Seleccione el Tipo de asunto.`;
      if (!point.responsables.length) return `${label} — Seleccione al menos un responsable.`;
      if (!CLASSIFICATIONS.includes(point.clasificacion)) return `${label} — Seleccione la clasificación.`;
      if (point.requiere_seguimiento && !point.fecha_compromiso) return `${label} — La fecha compromiso es obligatoria.`;
      const existing = communication?.puntos.find((p) => p.id === point.id);
      const sameAntecedent = existing && existing.antecedente_doc_id === point.antecedente_doc_id && existing.antecedente_punto_id === point.antecedente_punto_id;
      const scopeWasSelected = sameAntecedent || Boolean(antecedentScopeSelected[point.clientId]);
      if (["Seguimiento", "Cierre"].includes(point.clasificacion) && (!point.antecedente_doc_id || !scopeWasSelected || (!sameAntecedent && !nodes.some((node) => node.antecedente_doc_id === point.antecedente_doc_id && node.antecedente_punto_id === point.antecedente_punto_id)))) return `${label} — Seleccione la minuta antecedente y después el alcance del seguimiento.`;
      const original = communication?.puntos.find((item) => item.id === point.id);
      if (original?.requiere_seguimiento && point.requiere_seguimiento && original.fecha_compromiso !== point.fecha_compromiso && !point.motivo_reprogramacion?.trim()) return `${label} — Indique el motivo de la reprogramación.`;
    }
    if (!communication && !file) return "Debe adjuntar el PDF de la comunicación.";
    return "";
  }
  async function submit(event: FormEvent) {
    event.preventDefault();
    if (!context) return;
    const validationError = validateValues();
    if (validationError) { setError(validationError); return; }
    if (followUpParent && !followUpParentIsMinute && !followUpTypeId) { setError("El tipo de seguimiento es obligatorio."); return; }
    if (isMinute && followUpParent && !followUpParentIsMinute && followUpPointId && !followUpTargetPointClientId) { setError("Seleccione el punto de la nueva comunicación que dará seguimiento al punto origen."); return; }
    setBusy(true); setError("");
    try {
      const submittedValues = prepareCommunicationValues({
        ...values,
        area_id: isAdmin ? values.area_id : String(context.area_id || ""),
        actividad_material_id: communication ? values.actividad_material_id : [],
      }, isMinute, participantChoices, participantCustom);
      if (communication) {
        await updateCommunication(String(communication.id), submittedValues);
        if (file) await replaceCommunicationPdf(String(communication.id), file);
      } else await createCommunication(
        context,
        submittedValues,
        file as File,
        followUpParent ? {
          parentId: String(followUpParent.id),
          typeId: followUpTypeId || undefined,
          pointId: followUpPointId,
          targetPointOrdinal: isMinute && followUpPointId
            ? submittedValues.puntos.findIndex((point) => point.clientId === followUpTargetPointClientId) + 1
            : null,
        } : undefined,
      );
      await onSaved();
    } catch (caught) { setError(getErrorMessage(caught)); }
    finally { setBusy(false); }
  }
  return <Modal title={communication ? "Editar comunicación oficial" : followUpParent ? `Nueva comunicación de seguimiento de ${String(followUpParent.folio || "")}` : "Nueva comunicación oficial"} onClose={onClose}>
    <form className="form-grid communication-form" onSubmit={submit}>
      {error && <div className="notice error full">{error}</div>}
      {followUpParent && <div className="notice info full">La nueva comunicación quedará vinculada automáticamente con <strong>{String(followUpParent.folio || "")}</strong>.</div>}
      <div className="communication-form-row communication-date-folio full">
        <label>Fecha<ExecutionCivilDateInput min={followUpParent ? nextCivilDate(followUpParent.fecha) || undefined : undefined} max={localToday()} value={String(values.fecha)} onChange={(value) => set("fecha", value)} required /></label>
        <label>Folio<input value={String(values.folio)} onChange={(event) => set("folio", event.target.value)} required /></label>
      </div>
      <label className="full">Título<input value={values.titulo} maxLength={500} onChange={(event) => set("titulo", event.target.value)} required /></label>
      <div className="communication-form-row communication-meta-row full">
        <label className="communication-type-field">Tipo de comunicación<select value={String(values.tipo_doc_id)} onChange={(event) => {
          const typeId = event.target.value;
          const minute = isMinuteDocument(rowLabel(types.find((row) => rowValue(row) === typeId) || {}));
          setValues((current) => ({ ...current, tipo_doc_id: typeId, ...(!minute ? { autor_user_id: null, autor_nombre: null, participantes: [] } : {}) }));
          if (!minute) { setParticipantChoices([]); setParticipantCustom(""); }
        }} required><option value="">Selecciona…</option>{types.map((row) => <option key={rowValue(row)} value={rowValue(row)}>{rowLabel(row)}</option>)}</select></label>
        {isAdmin && areas.length > 0 && <label>Área<select value={String(values.area_id)} onChange={(event) => { const areaId = event.target.value; setValues((current) => ({ ...current, area_id: areaId, ...(current.area_id !== areaId ? { autor_user_id: null, autor_nombre: null } : {}) })); }} required><option value="">Selecciona…</option>{areas.map((row) => <option key={rowValue(row)} value={rowValue(row)}>{rowLabel(row)}</option>)}</select></label>}
        {!isAdmin && <div className="readonly-area"><span className="field-label">Área</span><strong>{context?.area_nombre || "Área asignada"}</strong></div>}
      </div>
      <div className="communication-form-row communication-front-row full">
        <label>Frente<select value={String(values.frente_id)} onChange={(event) => { set("frente_id", event.target.value); set("subfrente_id", []); }}><option value="">Todos los frentes</option>{fronts.map((row) => <option key={rowValue(row)} value={rowValue(row)}>{rowLabel(row)}</option>)}</select></label>
        <div className="form-field"><span className="field-label">Subfrente</span><MultiSelect disabled={!values.frente_id} values={stringArray(values.subfrente_id)} options={catalogOptions(subfronts)} onChange={(value) => set("subfrente_id", value)} placeholder="Todos los subfrentes" /></div>
      </div>
      <div className="communication-form-row communication-party-row full">
        <label>Emisor<select value={emisorSelector} onChange={(event) => { const selector = event.target.value as CommunicationPartySelector; setEmisorSelector(selector); setValues((current) => ({ ...current, emisor: selector === "Otro" ? "" : selector, ...(selector !== "Supervisión" ? { autor_user_id: null, autor_nombre: null } : {}) })); }} required><option value="">Selecciona…</option>{COMMUNICATION_PARTIES.map((party) => <option key={party} value={party}>{party}</option>)}<option value="Otro">Otro</option></select></label>
        {isMinute ? <div className="form-field"><span className="field-label">Participantes</span><MultiSelect values={participantChoices} options={MINUTE_PARTICIPANTS.map((party) => ({ value: party, label: party }))} onChange={setParticipantChoices} /></div>
          : <span />}
      </div>
      {(emisorSelector === "Otro" || needsAuthor || (isMinute && participantChoices.includes("Otro"))) && <div className="communication-form-row communication-conditional-row full">
        {emisorSelector === "Otro" && <label>{isMinute ? "Entidad emisora" : "Especifique el emisor"}<input value={values.emisor} maxLength={150} onChange={(event) => set("emisor", event.target.value)} required /></label>}
        {needsAuthor && <label>Autor<select aria-label="Autor" value={values.autor_user_id || ""} onChange={(event) => {
          const person = personnel.find((item) => item.user_id === event.target.value);
          setValues((current) => ({ ...current, autor_user_id: person?.user_id || null, autor_nombre: person?.nombre || null }));
        }} required disabled={personnelLoading}><option value="">{personnelLoading ? "Cargando personal…" : "Seleccione un autor"}</option>
          {historicalAuthor && !personnel.some((person) => person.user_id === values.autor_user_id) && <option value={values.autor_user_id || ""}>{values.autor_nombre || "Autor histórico"} (histórico)</option>}
          {personnel.map((person) => <option key={person.user_id} value={person.user_id}>{person.nombre}</option>)}
        </select>{personnelError && <small role="alert">No se pudo cargar el personal: {personnelError}</small>}</label>}
        {isMinute && participantChoices.includes("Otro") && <label>Entidad participante<input aria-label="Entidad participante" value={participantCustom} onChange={(event) => setParticipantCustom(event.target.value)} required /><small>Separe varias entidades con punto y coma.</small></label>}

      </div>}
      {followUpParent && !followUpParentIsMinute && <label className="full">Tipo de seguimiento<select value={followUpTypeId} onChange={(event) => setFollowUpTypeId(event.target.value)} required><option value="">Selecciona…</option>{followUpTypes.map((row) => <option key={rowValue(row)} value={rowValue(row)}>{rowLabel(row)}</option>)}</select></label>}
      {communication
        ? <div className="communication-form-row communication-deliverables-row full">
            <div className="form-field readonly-area communication-material-field"><span className="field-label">Material / Trabajo</span><strong>{historicalMaterialText}</strong><small>Dato histórico. Ya no es editable.</small></div>
            <div className="form-field"><span className="field-label">Entregable</span><MultiSelect values={values.entregable_id} options={deliverables.map((deliverable) => ({ value: deliverable.id, label: deliverable.nombre }))} onChange={(value) => set("entregable_id", value)} /></div>
          </div>
        : <div className="form-field full communication-deliverable-new"><span className="field-label">Entregable</span><MultiSelect values={values.entregable_id} options={deliverables.map((deliverable) => ({ value: deliverable.id, label: deliverable.nombre }))} onChange={(value) => set("entregable_id", value)} /></div>}
      {communication && <div className="communication-form-row communication-support-row full">
        <div className="form-field"><span className="field-label">Estimaciones</span><MultiSelect values={values.estimaciones_id} options={catalogOptions(estimations, (row) => String(row.numero || rowLabel(row)))} onChange={(value) => set("estimaciones_id", value)} /></div>
        <div className="form-field readonly-area communication-status-field"><span className="field-label">Estatus actual</span><strong>{displayStoredCommunicationStatus(communication.estatus)}</strong></div>
      </div>}
      <label className="full">Resumen<textarea rows={5} value={String(values.resumen)} onChange={(event) => set("resumen", event.target.value)} required /></label>
      {!isMinute && <section className="communication-points communication-recipients full"><strong>Destinatarios</strong>
        {values.destinatarios.map((recipient, index) => <div className="communication-point-card communication-recipient-card" key={recipient.id || index}>
          <div className="communication-recipient-row">
            <label>Tipo<select value={recipient.tipo_destinatario} onChange={(event) => set("destinatarios", values.destinatarios.map((r, i) => i === index ? { ...r, tipo_destinatario: event.target.value as typeof r.tipo_destinatario, nombre_otro: null } : r))}>{MINUTE_PARTICIPANTS.map((party) => <option key={party}>{party}</option>)}</select></label>
            <label>Fecha de acuse de recepción<ExecutionCivilDateInput min={values.fecha || undefined} value={recipient.fecha_recepcion || ""} onChange={(value) => set("destinatarios", values.destinatarios.map((r, i) => i === index ? { ...r, fecha_recepcion: value || null } : r))} /></label>
          </div>
          {recipient.tipo_destinatario === "Otro" && <label className="communication-recipient-other">Nombre<input required value={recipient.nombre_otro || ""} onChange={(event) => set("destinatarios", values.destinatarios.map((r, i) => i === index ? { ...r, nombre_otro: event.target.value } : r))} /></label>}
          <button type="button" className="button-link point-remove" onClick={() => set("destinatarios", values.destinatarios.filter((_, i) => i !== index))}>Eliminar destinatario</button>
        </div>)}
        <button type="button" className="button secondary" onClick={() => set("destinatarios", [...values.destinatarios, { tipo_destinatario: "Contratista", nombre_otro: null, fecha_recepcion: null }])}>+ Agregar destinatario</button>
        <div className="communication-response-row">
          <label>¿Requiere respuesta?<select value={String(values.requiere_respuesta)} onChange={(event) => setValues((current) => ({ ...current, requiere_respuesta: event.target.value === "true", ...(event.target.value !== "true" ? { dias_contestacion: null } : {}) }))}><option value="false">No</option><option value="true">Sí</option></select></label>
          <label>Días naturales máximos para contestación<input type="number" min={1} step={1} disabled={!values.requiere_respuesta} value={values.requiere_respuesta ? values.dias_contestacion ?? "" : ""} onChange={(event) => set("dias_contestacion", event.target.value ? Number(event.target.value) : null)} /><small>Opcional. Si se deja vacío, no se calculará vencimiento.</small></label>
        </div>
        <span className="point-status">Estatus: {communication?.estatus || "Se asignará al guardar"}</span>
      </section>}
      {isMinute && <section className="communication-points full" aria-labelledby="communication-points-title">
        <div className="communication-points-heading"><div><strong id="communication-points-title">Puntos</strong><span>Registre uno o varios compromisos de la comunicación.</span></div><button type="button" className="button secondary small" onClick={() => set("puntos", [...values.puntos, emptyCommunicationPoint()])}>+ Agregar punto</button></div>
        <div className="communication-points-list">{values.puntos.map((point, index) => {
          const originalPoint = communication?.puntos.find((item) => item.id === point.id);
          const sameRegisteredAntecedent = Boolean(originalPoint && originalPoint.antecedente_doc_id === point.antecedente_doc_id && originalPoint.antecedente_punto_id === point.antecedente_punto_id);
          const selectedMinute = followUpMinutes.find((minute) => minute.id === point.antecedente_doc_id);
          const scopeSelected = Boolean(antecedentScopeSelected[point.clientId]) || sameRegisteredAntecedent;
          const scopeValue = scopeSelected ? (point.antecedente_punto_id || "__whole__") : "";
          return <article className="communication-point-card" key={point.clientId}>
            <header><strong>Punto {index + 1}</strong><button type="button" className="button-link point-remove" aria-label={`Eliminar punto ${index + 1}`} onClick={() => set("puntos", values.puntos.filter((item) => item.clientId !== point.clientId))}>Eliminar</button></header>
            <label className="point-description">Punto / descripción<textarea rows={3} value={point.punto} onChange={(event) => updatePoint(point.clientId, { punto: event.target.value })} required /></label>

            <div className="communication-point-primary-row">
              <label>Clasificación<select required value={point.clasificacion} onChange={(event) => updatePoint(point.clientId, { clasificacion: event.target.value as PointClassification })}>{CLASSIFICATIONS.map((value) => <option key={value}>{value}</option>)}</select></label>
              <label>Tipo de asunto<select value={point.tipo_asunto_id} onChange={(event) => updatePoint(point.clientId, { tipo_asunto_id: event.target.value })} required><option value="">Selecciona…</option>{subjects.map((row) => <option key={rowValue(row)} value={rowValue(row)}>{rowLabel(row)}</option>)}</select></label>
            </div>

            <div className="communication-point-secondary-row">
              <div className="form-field"><span className="field-label">Responsables</span><MultiSelect values={point.responsables} options={COMMUNICATION_PARTIES.map((party) => ({ value: party, label: party }))} onChange={(responsables) => updatePoint(point.clientId, { responsables: responsables as CommunicationPartyPreset[] })} /></div>
              <label>¿Requiere seguimiento?<select disabled={["Informativo", "Cierre"].includes(point.clasificacion)} value={String(point.requiere_seguimiento)} onChange={(event) => updatePoint(point.clientId, { requiere_seguimiento: event.target.value === "true", fecha_compromiso: "" })}><option value="true">Sí</option><option value="false">No</option></select></label>
              <label>Fecha de compromiso{point.requiere_seguimiento
                ? <ExecutionCivilDateInput required value={point.fecha_compromiso} onChange={(value) => updatePoint(point.clientId, { fecha_compromiso: value })} />
                : <input className="communication-disabled-date" value="" placeholder="DD/MM/YYYY" disabled />}</label>
            </div>

            {["Seguimiento", "Cierre"].includes(point.clasificacion) && <div className="communication-point-antecedent-row">
              <label>Minuta antecedente
                <select required value={point.antecedente_doc_id || ""} onChange={(event) => {
                  const docId = event.target.value || null;
                  updatePoint(point.clientId, { antecedente_doc_id: docId, antecedente_punto_id: null });
                  setAntecedentScopeSelected((current) => ({ ...current, [point.clientId]: false }));
                }}>
                  <option value="">Seleccione el folio…</option>
                  {point.antecedente_doc_id && !followUpMinutes.some((minute) => minute.id === point.antecedente_doc_id) && <option value={point.antecedente_doc_id}>Antecedente registrado</option>}
                  {followUpMinutes.map((minute) => <option key={minute.id} value={minute.id}>{minute.folio}</option>)}
                </select>
              </label>
              <label>Alcance del antecedente
                <select required disabled={!point.antecedente_doc_id} value={scopeValue} onChange={(event) => {
                  const value = event.target.value;
                  setAntecedentScopeSelected((current) => ({ ...current, [point.clientId]: Boolean(value) }));
                  updatePoint(point.clientId, { antecedente_punto_id: value === "__whole__" || !value ? null : value });
                }}>
                  <option value="">Seleccione…</option>
                  {sameRegisteredAntecedent && !selectedMinute && <option value={point.antecedente_punto_id || "__whole__"}>Antecedente registrado</option>}
                  {selectedMinute?.nodes.filter((node) => node.antecedente_punto_id === null).map((node) => <option key={`whole-${node.antecedente_doc_id}`} value="__whole__">Toda la minuta</option>)}
                  {selectedMinute?.nodes.filter((node) => node.antecedente_punto_id !== null).map((node, pointIndex) => <option key={node.antecedente_punto_id || pointIndex} value={node.antecedente_punto_id || ""}>{`Punto ${pointIndex + 1} · ${(node.punto || "Sin descripción").slice(0, 120)}`}</option>)}
                </select>
              </label>
              {nodesError && <small role="alert" className="communication-antecedent-error">{nodesError}</small>}
            </div>}

            {point.id && point.requiere_seguimiento && originalPoint?.fecha_compromiso !== point.fecha_compromiso && <label className="communication-reprogram-reason">Motivo de la reprogramación<textarea required value={point.motivo_reprogramacion || ""} onChange={(event) => updatePoint(point.clientId, { motivo_reprogramacion: event.target.value })} /></label>}
            <span className="point-status communication-point-auto-status">Estatus: {communication ? point.estatus : !point.requiere_seguimiento ? "Cerrado" : "Se asignará al guardar"}</span>
          </article>;
        })}</div>
      </section>}
      {isMinute && followUpParent && !followUpParentIsMinute && followUpPointId && <div className="form-field full followup-target-new-point">
        <span className="field-label">Punto que dará seguimiento</span>
        <select value={followUpTargetPointClientId} onChange={(event) => setFollowUpTargetPointClientId(event.target.value)} required>
          <option value="">Seleccione un punto de la nueva comunicación…</option>
          {values.puntos.map((point, index) => <option key={point.clientId} value={point.clientId}>{`Punto ${index + 1} — ${point.punto.trim().slice(0, 90) || "Sin descripción"}`}</option>)}
        </select>
        {followUpTargetPointClientId && <div className="followup-selected-point"><span>Punto destino</span><strong>{values.puntos.find((point) => point.clientId === followUpTargetPointClientId)?.punto || "—"}</strong></div>}
      </div>}
      <label className="full">Documento PDF de la comunicación<input aria-label="Documento PDF de la comunicación" type="file" accept="application/pdf,.pdf" onChange={(event) => setFile(event.target.files?.[0] || null)} required={!communication} />{communication && <small>Si selecciona un archivo, sustituirá el PDF actual al guardar los cambios.</small>}</label>
      <label className="full">Anexos PDF de la comunicación<input aria-label="Anexos PDF de la comunicación" type="file" accept="application/pdf,.pdf" multiple disabled /><small>La carga de anexos se habilitará posteriormente.</small></label>
      <div className="modal-actions full"><button type="button" className="button ghost" onClick={onClose}>Cancelar</button><button className="button primary" disabled={busy}>{busy ? "Guardando…" : communication ? "Guardar cambios" : "Crear comunicación"}</button></div>
    </form>
  </Modal>;
}

function CommunicationsEstimationsModal({ catalog, onClose }: { catalog: JsonRecord; onClose: () => void }) {
  const { context } = useSession();
  const [rows, setRows] = useState<JsonRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [frontId, setFrontId] = useState("");
  const [onlyAfterEstimation, setOnlyAfterEstimation] = useState(false);
  const fronts = arrayFrom(catalog, ["frentes"]);

  async function loadRelations() {
    if (!context) return;
    setLoading(true);
    setError("");
    try {
      setRows(await relationComEst(context, { frontId, onlyAfterEstimation }));
    } catch (caught) {
      setError(getErrorMessage(caught));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { void loadRelations(); }, [context, frontId, onlyAfterEstimation]);

  const visibleRows = useMemo(() => rows.filter((row) => {
    const date = String(row.fecha || "");
    return (!start || date >= start)
      && (!end || date <= end);
  }).map((row) => ({
    id: row.id,
    fecha: row.fecha,
    tipo: row.tipo_doc,
    folio: row.folio,
    estatus: row.estatus,
    frente: row.frente,
    subfrentes: row.subfrente,
    estimaciones: row.estimaciones,
  })), [rows, start, end]);

  return <Modal title="Relación con las estimaciones" className="modal-wide" onClose={onClose}>
    <div className="relation-modal-body">
      <div className="relation-filters">
        <label>Desde<ExecutionCivilDateInput max={end || localToday()} value={start} onChange={(value) => setStart(value)} /></label>
        <label>Hasta<ExecutionCivilDateInput min={start || undefined} max={localToday()} value={end} onChange={(value) => setEnd(value)} /></label>
        <label>Frente<select value={frontId} onChange={(event) => setFrontId(event.target.value)}><option value="">Todos</option>{fronts.map((row) => <option key={rowValue(row)} value={rowValue(row)}>{rowLabel(row)}</option>)}</select></label>
        <label>Mostrar<select value={onlyAfterEstimation ? "after" : "all-linked"} onChange={(event) => setOnlyAfterEstimation(event.target.value === "after")}><option value="all-linked">Todas las comunicaciones con estimación</option><option value="after">Solo comunicaciones posteriores a estimación</option></select></label>
        <button type="button" className="icon-button relation-refresh" title="Actualizar" aria-label="Actualizar relación" onClick={() => void loadRelations()}><RefreshCw size={18} /></button>
      </div>
      {error && <ErrorNotice message={error} />}
      <div className="section-meta"><strong>Comunicaciones y estimaciones</strong><span>{visibleRows.length} registros</span></div>
      {loading ? <Loading /> : <DataTable rows={visibleRows} columns={["fecha", "tipo", "folio", "estatus", "frente", "subfrentes", "estimaciones"]} tableClassName="relations-table" renderCell={(row, key) => key === "fecha" ? formatCivilDate(row.fecha) : undefined} />}
    </div>
  </Modal>;
}
